import { getTranslation } from "Utils/getTranslation"
import { rebaserColonnesDeMois } from "Utils/monthColumnsYear"
import { ajouterChampsLibelle, construireLibelles, sortiesLibelle } from "Utils/optionLabel"
import { isEqual, cloneDeep, toString, find } from "lodash"
import { getFieldsFromUUIDs } from "Utils/preparationUtils"
import { fieldsAreRelated } from "Utils/dataCatalogueUtils.js"
import { temporaryIdStr } from "Components/WidgetSettingsComponent/widgetSettingsUtils"
import moment from "moment"

import { exportDatas, isObjectV2, toNumber } from "Utils/global.js"
import { getFieldFromUUID } from "Utils/dataCatalogueUtils"
import { getMonthNameWithMonthNumber } from "Utils/dateUtils"
import { getDefaultDataWidget, getScaleAndEnumColors } from "./widgetDataUtils"

import { getPageImageUrl } from "Utils/useImage"
import { savedWidgetImage } from "Utils/widgetImage"

export const widgetCategory = {
  CHART: "chart",
  TEXT: "text",
  FILTER: "filter",
}

export const sortingTypes = {
  ALL_RESULT: "allResult",
  BEST_RESULT: "bestResult",
  WORST_RESULT: "worstResult",
}

export const barChartTypeList = [
  "stackedBar",
  "stackedBar100",
  "histogram",
  "horizontalBarChart",
  "pyramide",
]
export const barChartList = ["stackedBar", "stackedBar100", "histogram", "pyramide"]
export const barChartOrientationList = ["horizontal", "vertical"]
export const simplePieChartTypeList = ["pieChart", "semiPieChart"]
export const doublePieChartTypeList = ["doublePieChart", "doubleSemiPieChart"]
export const lineChartTypeList = ["lineChart", "stackedArea", "cumulativeLineChart"]

// Listes des widgets autorisé pour le tri (Croissant/Décroissant)
export const isPossibleTypeToSort = [
  "lineChart",
  "histogram",
  "stackedBar",
  "horizontalBarChart",
  "pyramide",
  "dropdownList",
  "cumulativeLineChart",
  "stackedArea",
]

export const authorizedWidgetTypeToCompare = [
  "table",
  "lineChart",
  "histogram",
  "stackedBar",
  "counter",
  "radarChart",
  "pieChart",
  "progressBar",
  "multiGauge",
]

export const authorizedWidgetTypeToFilter = ["dropdownList", "searchBar"]

export const isPossibleTypeToSortBestResult = [
  "progressBar",
  "histogram",
  "stackedBar",
  "pieChart",
  "table",
  "ag_table",
  "radarChart",
  "treeMap",
  "multiGauge",
]

export const availableColorTheme = {
  palette1: [
    "#68B8DC",
    "#6794DC",
    "#6771DC",
    "#8067DC",
    "#A367DC",
    "#C767DC",
    "#DC67CE",
    "#DC67AB",
    "#DC6788",
    "#DC6967",
    "#DC8C67",
    "#DCAF67",
  ],
  palette2: [
    "#3DAAE7",
    "#1505B1",
    "#3B5869",
    "#2F84B5",
    "#84C6EC",
    "#B57A05",
    "#FFAA00",
    "#FFD400",
    "#9C282C",
    "#01014B",
  ],
  palette3: [
    "#6E7783",
    "#7C7877",
    "#77AF9C",
    "#77AAAD",
    "#9DC3C1",
    "#ABD0CE",
    "#D8E6E7",
    "#D9D4CF",
    "#E0E3DA",
    "#F0E5DE",
  ],
  palette4: [
    "#293250",
    "#902C2E",
    "#D5433D",
    "#CC4736",
    "#F05540",
    "#F26958",
    "#F47E6F",
    "#F59487",
    "#F9BEB6",
    "#FBD3CE",
    "#D32711",
  ],
  palette5: [
    "#66738E",
    "#766D91",
    "#785670",
    "#523C58",
    "#813B3D",
    "#BD5F52",
    "#EF8B78",
    "#F9C885",
    "#EBA05C",
    "#9B5134",
    "#66738E",
    "#766D91",
  ],
  palette6: ["#87A4D9", "#FF6601", "#CDCED0", "#FFBE3D", "#385CA0", "#F4B183", "#454F54"],
}

export const isWidgetChart = widgetObject => {
  return widgetObject?.widgetCategory === widgetCategory.CHART
}

export const isTextWidget = widgetObject => {
  return widgetObject?.widgetCategory === widgetCategory.TEXT
}

export const isFilterWidget = widgetObject => {
  return widgetObject?.widgetCategory === widgetCategory.FILTER
}
export const isGoogleMapWidget = widgetObject => {
  return widgetObject?.chartType === "googleMap"
}

export const isRedirectButtonWidget = widgetObject => {
  return widgetObject?.widgetType === "redirectButton"
}
export const isEmptyWidget = widgetObject => {
  const isDataWidgetEmpty = !isTextWidget(widgetObject) && widgetObject?.data?.length === 0
  // Text Widgets Exceptions
  const isBasicTxtEmpty =
    widgetObject?.widgetType === "basicText" &&
    widgetObject?.widgetProps?.textAreaValue === "" &&
    widgetObject?.title === ""
  const isRedirectBtnEmpty =
    widgetObject?.widgetType === "redirectButton" &&
    ((widgetObject?.widgetProps?.redirectButtonIsPageUrl &&
      widgetObject?.widgetProps?.redirectButtonPageUrl === "") ||
      (widgetObject?.widgetProps?.redirectButtonIsUrl &&
        widgetObject?.widgetProps?.redirectButtonUrl === "") ||
      (widgetObject?.widgetProps?.redirectButtonIsReferentiel &&
        widgetObject?.widgetProps?.redirectButtonReferentiel === ""))
  const isImageEmpty =
    widgetObject?.widgetType === "image" &&
    widgetObject?.widgetProps?.urlImage === "" &&
    widgetObject?.widgetProps?.tempUrlImage === ""

  const isActualityListEmpty =
    widgetObject?.widgetType === "actualityList" && widgetObject?.title === ""

  const isTextWidgetEmpty =
    isBasicTxtEmpty || isRedirectBtnEmpty || isImageEmpty || isActualityListEmpty

  return isDataWidgetEmpty || isTextWidgetEmpty
}

const listPropertyThatCanBeApplied = [
  "visibilityLegend",
  "isFullScreenable",
  "numberDecimal",
  "showSpaceForThousand",
  "decimalSeparator",
  "activeBackground",
  "activeBorder",
  "activeRadius",
  "backgroundColor",
  "borderColor",
  "borderRadius",
  "borderSize",
  "borderActivation",
  "fontColor",
  "labelColor",
  "titleColor",
  "titleWeight",
  "titleSize",
  "titleAlignement",
  "colorSetTheme",
  "colorSetThemeName",
  "colorMapper",
]

const listPropertyThatMustBeApplied = [
  "titleColor",
  "titleWeight",
  "titleSize",
  "activeBackground",
  "activeBorder",
  "activeRadius",
  "borderColor",
  "borderSize",
]
export const getNewWidgetListAfterApplyStyle = (widgetInfo, previousWidgetList) => {
  if (!widgetInfo || !Array.isArray(previousWidgetList)) return []
  const actualWidgetProps = widgetInfo?.widgetProps

  var newWidgetList = previousWidgetList.map(widget => {
    var newWidgetProps = { ...widget.widgetProps }
    listPropertyThatCanBeApplied.forEach(property => {
      var destinationWidgetHasProperty = widget.widgetProps?.hasOwnProperty(property)
      var originWidgetHasProperty = actualWidgetProps?.hasOwnProperty(property)
      var bothWidgetsHasProperty = destinationWidgetHasProperty && originWidgetHasProperty
      var propertyMustBeApplied =
        listPropertyThatMustBeApplied.includes(property) && originWidgetHasProperty

      if (bothWidgetsHasProperty || propertyMustBeApplied) {
        newWidgetProps[property] = actualWidgetProps[property]
      }
    })
    return {
      ...widget,
      widgetProps: newWidgetProps,
    }
  })
  return newWidgetList
}

export const getWidgetNameTraduction = (widgetType, partialTrad) => {
  if (widgetType === undefined || partialTrad === undefined) return "Undefined Widget Name"
  else if (partialTrad?.[widgetType] === undefined) return widgetType
  return partialTrad[widgetType]
}

export const getMappingWidgetType_Name = trad => {
  const {
    pieChart,
    stackedBar,
    lineChart,
    cumulativeLineChart,
    stackedArea,
    dropdownList,
    counter,
    histogram,
    pyramide,
    table,
    ag_table,
    CALENDAR,
    RADAR_CHART,
    CARTOGRAPHIE,
    MAPPING_FILTER,
    horizontalBarChart,
    BASIC_TEXT,
    WIDGET_REDIRECT_BUTTON_NAME,
    WIDGET_IMAGE_NAME,
    gaugeChart,
    DOUBLE_PROGRESS_BAR,
    treeMap,
    multiGauge,
    multiTabWidget,
    evolutionWidget,
    heatMap,
    heatMapRisk,
    actualityList,
    HIERARCHICAL_FILTER,
    GANTT_CHART,
  } = trad

  return {
    pieChart,
    stackedBar,
    lineChart,
    cumulativeLineChart,
    stackedArea,
    dropdownList,
    counter,
    histogram,
    pyramide,
    table,
    ag_table,
    calendar: CALENDAR,
    radarChart: RADAR_CHART,
    mapping: CARTOGRAPHIE,
    mappingFilter: MAPPING_FILTER,
    horizontalBarChart,
    basicText: BASIC_TEXT,
    redirectButton: WIDGET_REDIRECT_BUTTON_NAME,
    image: WIDGET_IMAGE_NAME,
    gaugeChart,
    progressBar: DOUBLE_PROGRESS_BAR,
    treeMap,
    multiGauge,
    multiTabWidget,
    evolutionWidget,
    heatMap,
    heatMapRisk,
    actualityList,
    hierarchicalFilter: HIERARCHICAL_FILTER,
    ganttChartV2: GANTT_CHART,
  }
}

export const widgetsToHide = [
  "horizontalBarChart",
  "stackedArea",
  "stackedBar",
  "pyramide",
  "cumulativeLineChart",
  "fillingGauge",
  "table",
]

export const getAvailableWidgetsListToShow = (allWidget, widgetCategory, partialTrad) => {
  var filteredWidgetList = allWidget.filter(widget => {
    let widgetShouldBeHidden = widgetsToHide.includes(widget.type)
    return widget.category === widgetCategory && !widgetShouldBeHidden
  })
  return filteredWidgetList.map(widgetObj => {
    var nameTrad = getWidgetNameTraduction(widgetObj.type, partialTrad)
    return {
      ...widgetObj,
      name: nameTrad,
    }
  })
}

export const getWidgetsListAutorized = (widgetsList, myBusiness) => {
  // Actuellement Widget ganttChart uniquement sur MSF
  const widgetsListFilterMSF = myBusiness?.useMSF
    ? widgetsList
    : widgetsList?.filter(item => item?.type !== "ganttChart" && item?.type !== "caseCardMSF")
  // Widget Actualité uniquement pour la gouvernance V1
  const widgetsListFilterActuality = widgetsListFilterMSF?.filter(
    item => item?.type !== "actualityList"
  )
  const widgetsListFilter = myBusiness?.isGovernanceBusiness
    ? myBusiness?.useReferentials
      ? widgetsListFilterActuality
      : widgetsListFilterMSF
    : widgetsListFilterActuality

  return widgetsListFilter
}

export const getFieldIndexInDatacatalogue = (uuidList, fullFieldsData = []) => {
  var indexListToReturn = []
  uuidList.forEach(fieldUuid => {
    var fieldDataIndex = fullFieldsData.findIndex(field => field.uuid === fieldUuid)
    indexListToReturn.push(fieldDataIndex)
  })
  return indexListToReturn
}

export const getDefaultAggregationFuncName = widgetInfo => {
  // if (widgetInfo.widgetType === "gaugeChart") return "count"
  if (widgetInfo.widgetType === "mapping") return "count"

  var calculus = "sum"
  if (widgetInfo?.groupBy?.[0]?.calculus) calculus = widgetInfo.groupBy[0].calculus
  return calculus
}

const filterIsFromACalendar = (operations, values) => {
  return Array.isArray(operations) && operations?.length === 2 && values?.length === 2
}

export const availableFrequencyValue = [
  "default",
  "dayMonthYear",
  "dayMonth",
  "monthYear",
  "monthLetterYear",
  "day",
  "month",
  "quarter",
  "quarterYear",
  "year",
]

export const isValidFrequencyValue = freqValue => {
  var isValid = availableFrequencyValue.includes(freqValue)
  if (!isValid) console.error("frequencyValue is not Valid")
  return isValid
}

/**
 * Opérateurs qui n'attendent AUCUNE valeur.
 *
 * ⚠️ Le moteur les veut **sans clé `value`**, pas avec une valeur nulle : il rejette explicitement
 * une feuille `IS NULL` qui en porte une (`bi/utils/etl.utils.ts`). La garde ci-dessous et le
 * transformateur doivent donc s'accorder là-dessus — l'une pour les laisser passer, l'autre pour
 * omettre la clé.
 */
export const OPERATIONS_SANS_VALEUR = new Set(["IS NULL", "IS NOT NULL"])

/**
 * Un filtre de page est-il exploitable ?
 *
 * ⚠️ Cette garde protège contre un filtre à MOITIÉ CONSTRUIT — un champ choisi, pas encore de
 * valeur — qui produirait une condition absurde. Elle est partagée par tous les filtres du produit,
 * et son relâchement est strictement borné aux opérateurs qui n'attendent pas de valeur : ceux-là
 * ne peuvent pas être « incomplets », l'absence de valeur EST leur sens. Un filtre ordinaire à
 * valeur vide reste écarté exactement comme avant.
 */
const conditionsToUsePageFilterAreRespected = (fieldUUID, operation, value) => {
  if (fieldUUID === undefined || fieldUUID === "" || operation === undefined) return false
  if (OPERATIONS_SANS_VALEUR.has(operation)) return true
  if (value === undefined || value === null || value === "") return false
  return true
}

const getTransformedFilterFromPage = pageFilter => {
  var pageFilterWidgetIdList = Object.keys(pageFilter)
  var filterToReturn = []
  pageFilterWidgetIdList.forEach(widgetId => {
    const widgetFilter = pageFilter[widgetId]

    // Multi-field SearchBar: conditions array (OR across fields)
    if (Array.isArray(widgetFilter?.conditions) && widgetFilter.conditions.length > 0) {
      const fieldFilters = []
      widgetFilter.conditions.forEach(
        ({ fieldUUID, operation, value, case_sensitivity = true, ignore_accents = false }) => {
          if (!conditionsToUsePageFilterAreRespected(fieldUUID, operation, value)) return
          // ⚠️ Clé `value` ABSENTE, pas nulle : le moteur rejette un `IS NULL` qui en porte une.
          if (OPERATIONS_SANS_VALEUR.has(operation)) {
            fieldFilters.push({ fieldUUID, operation })
            return
          }
          if (Array.isArray(value)) {
            value.forEach(aValue =>
              fieldFilters.push({ fieldUUID, operation, value: aValue, case_sensitivity, ignore_accents })
            )
          } else {
            fieldFilters.push({ fieldUUID, operation, value, case_sensitivity, ignore_accents })
          }
        }
      )
      if (fieldFilters.length > 0) {
        filterToReturn.push({ operation: "or", filters: fieldFilters })
      }
      return
    }

    const {
      fieldUUID,
      operation,
      value,
      case_sensitivity = true,
      ignore_accents = false,
      filters,
    } = widgetFilter
    if (conditionsToUsePageFilterAreRespected(fieldUUID, operation, value)) {
      if (OPERATIONS_SANS_VALEUR.has(operation)) {
        filterToReturn.push({ fieldUUID, operation })
      } else if (filterIsFromACalendar(operation, value)) {
        filterToReturn.push({
          operation: "and",
          filters: value.map((aValue, index) => {
            return {
              fieldUUID,
              operation: operation[index],
              value: aValue,
            }
          }),
        })
      } else {
        if (Array.isArray(value)) {
          filterToReturn.push({
            operation: "or",
            filters: value.map(aValue => ({
              fieldUUID,
              operation,
              value: aValue,
              case_sensitivity,
              ignore_accents,
            })),
          })
        } else {
          filterToReturn.push({
            fieldUUID,
            operation,
            value,
            case_sensitivity,
            ignore_accents,
          })
        }
      }
    } else if (Array.isArray(filters)) {
      filterToReturn.push({
        operation: "or",
        filters: [...filters],
      })
    }
  })
  return filterToReturn
}

export const getFieldFrequencyArr = widgetInfo => {
  var fieldFrequencyArr = []
  widgetInfo.groupBy.forEach(fullField => {
    var frequency = fullField?.frequency
    if (frequency && isValidFrequencyValue(frequency) && frequency !== "default") {
      if (frequency === "dayMonthYear") {
        fieldFrequencyArr.push(
          [fullField.id, "day"],
          [fullField.id, "month"],
          [fullField.id, "year"]
        )
      } else if (frequency === "dayMonth") {
        fieldFrequencyArr.push([fullField.id, "day"], [fullField.id, "month"])
      } else if (["monthYear", "monthLetterYear"].includes(frequency)) {
        fieldFrequencyArr.push([fullField.id, "month"], [fullField.id, "year"])
      } else if (frequency === "quarterYear") {
        fieldFrequencyArr.push([fullField.id, "quarter"], [fullField.id, "year"])
      } else if (frequency === "quarter") {
        fieldFrequencyArr.push([fullField.id, "quarter"])
      } else if (frequency === "day") {
        fieldFrequencyArr.push([fullField.id, "day"])
      } else if (frequency === "month") {
        fieldFrequencyArr.push([fullField.id, "month"])
      } else if (frequency === "year") {
        fieldFrequencyArr.push([fullField.id, "year"])
      } else {
        fieldFrequencyArr.push([fullField.id, frequency])
      }
    }
  })
  return fieldFrequencyArr
}

export const getFirstWidgetUUID = widgetInfo => {
  var uuidToReturn = undefined
  if (widgetInfo?.data?.[0]) {
    uuidToReturn = widgetInfo?.data?.[0]?.uuid ? widgetInfo.data[0].uuid : widgetInfo.data[0].id
  } else if (widgetInfo?.groupBy?.[0]) {
    uuidToReturn = widgetInfo.groupBy[0].uuid ?? widgetInfo.groupBy[0].id
  }

  return uuidToReturn
}

export const fielduuidsAreRelated = (fielduuid1, fielduuid2, fieldsData) => {
  var field1 = fieldsData.find(field => field.uuid === fielduuid1)
  var field2 = fieldsData.find(field => field.uuid === fielduuid2)
  if (field1 === undefined || field2 === undefined) return false
  return fieldsAreRelated(field1, field2)
}

/**
 * Filtres de page qui s'appliquent à un widget.
 *
 * @param dependentFilters réglage de page « filtres dépendants » (écart E4). Quand il est ACTIF, une
 *   liste déroulante restreint ses options d'après les autres filtres : choisir un organisme ne
 *   laisse alors que les entités qui lui sont rattachées. Inactif par défaut — voir ci-dessous.
 */
export const getCorrectRelatedPageFilter = (
  widgetInfo,
  valueFilterSelected,
  fieldsData,
  dependentFilters = false
) => {
  const goodValueFilterSelected = getAllFilteredValueSelectedExceptOwn(
    widgetInfo,
    valueFilterSelected,
    dependentFilters
  )

  return getRelatedPageFilterOrComparison(widgetInfo, goodValueFilterSelected, fieldsData)
}

/**
 * Les filtres de page à appliquer, celui du widget lui-même toujours retiré.
 *
 * ⚠️ Le retrait de son PROPRE filtre (ligne ci-dessous) est ce qui rend l'opération sûre, et il est
 * indépendant du réglage : sans lui, une liste se restreindrait d'après sa propre sélection et
 * n'offrirait plus qu'une seule valeur — celle déjà choisie, donc impossible à changer.
 *
 * ⚠️ Une liste déroulante ne reçoit les autres filtres que si le réglage de page est ACTIF. Par
 * défaut elle charge ses options indépendamment, ce qui était le comportement unique jusqu'ici :
 * tous les dashboards existants gardent donc le leur, à l'identique.
 *
 * ⚠️ Le réglage vaut pour TOUS les filtres de la page, pas seulement ceux d'entité. Sur les cubes
 * de cette plateforme, `fieldsAreRelated` rend `true` pour presque toutes les paires — les champs
 * partagent la relation `main` — de sorte que la cascade y est générale, non ciblée. Choisir un
 * statut restreindra donc aussi la liste des contrôleurs. C'est le sens de « filtres dépendants »
 * de l'écart E4, mais c'est plus large que le seul besoin d'entités qui l'a motivé, et cela coûte
 * une requête par filtre à chaque sélection.
 */
const getAllFilteredValueSelectedExceptOwn = (
  widgetInfo,
  valueFilterSelected,
  dependentFilters = false
) => {
  const actualWidgetId = widgetInfo?._id?.$oid
  const pageFiltersToReturn = { ...valueFilterSelected }
  if (pageFiltersToReturn.hasOwnProperty(actualWidgetId)) delete pageFiltersToReturn[actualWidgetId]

  if (widgetInfo?.widgetCategory === widgetCategory.FILTER && !dependentFilters) return {}

  return pageFiltersToReturn
}

/**
 * Colonnes RÉELLES du cube que représente une colonne de widget, pour le test de parenté.
 *
 * Une colonne fabriquée par le pipeline `transform` (`mergeGroups`, `unpivot`) n'existe dans aucun
 * cube : `/etl/dashboard/:id/fields` ne la connaît pas. `fielduuidsAreRelated` ne la trouve donc
 * pas dans `fieldsData` et rend `false` — le widget cessait silencieusement d'obéir aux filtres de
 * la page, quel que soit le filtre.
 *
 * La parenté d'une colonne fusionnée est celle de ses SOURCES : une colonne qui empile les
 * libellés de cinq familles est parente de tout ce dont l'une de ces cinq est parente. On remonte
 * donc au pipeline pour retrouver les colonnes d'origine, qui, elles, sont bien dans `fieldsData`.
 *
 * @returns les uuid à confronter au filtre. Une colonne réelle se représente elle-même.
 */
export const resolveFieldUuidSources = (widgetInfo, fieldUUID, fieldsData) => {
  if (fieldUUID === undefined) return []
  const isRealField = (fieldsData || []).some(field => field?.uuid === fieldUUID)
  if (isRealField) return [fieldUUID]

  const steps = widgetInfo?.request?.transform
  if (!Array.isArray(steps)) return [fieldUUID]

  const sources = []
  steps.forEach(step => {
    if (step?.op === "mergeGroups") {
      const groups = Array.isArray(step.groups) ? step.groups : []
      const positions = Array.isArray(step.as) ? step.as : []
      const index = positions.indexOf(fieldUUID)
      if (index !== -1) {
        // Une seule position : `as[k]` est alimenté par `groups[i].cols[k]`.
        groups.forEach(group => {
          const col = group?.cols?.[index]
          if (col) sources.push(col)
        })
      } else if (step.asKey === fieldUUID) {
        // La clé de groupe ne vient d'aucune colonne : elle nomme la provenance. Elle représente
        // donc TOUTES les colonnes sources — un filtre parent de l'une d'elles la concerne.
        groups.forEach(group => (group?.cols || []).forEach(col => col && sources.push(col)))
      }
    } else if (step?.op === "unpivot") {
      if (step.asValue === fieldUUID || step.asKey === fieldUUID) {
        ;(step.cols || []).forEach(col => col && sources.push(col))
      }
    }
  })

  // Aucune source identifiée : on rend la colonne telle quelle plutôt qu'un tableau vide, pour que
  // le comportement reste celui d'avant sur les pipelines que cette fonction ne sait pas remonter.
  return sources.length ? [...new Set(sources)] : [fieldUUID]
}

const getRelatedPageFilterOrComparison = (widgetInfo, valueFilterSelected, fieldsData) => {
  var pageFilterKeys = Object.keys(valueFilterSelected)

  var firstWidgetUuid = getFirstWidgetUUID(widgetInfo)
  if (firstWidgetUuid === undefined) return {}
  // Une colonne fabriquée par le pipeline est remplacée par ses colonnes sources : sans cela le
  // widget n'obéit à AUCUN filtre de page (cf. `resolveFieldUuidSources`).
  var widgetUuidsToTest = resolveFieldUuidSources(widgetInfo, firstWidgetUuid, fieldsData)
  var relatedPageFilter = {}
  pageFilterKeys.forEach(key => {
    let filterUuidToTest = valueFilterSelected[key].fieldUUID
    let isRelated = widgetUuidsToTest.some(uuid =>
      fielduuidsAreRelated(filterUuidToTest, uuid, fieldsData)
    )
    if (isRelated) {
      relatedPageFilter[key] = { ...valueFilterSelected[key] }
    }
  })

  return relatedPageFilter
}

export const isWidgetNeedToReload = (widgetInfo, dataChangeType) => {
  const widgetTypeThatDontNeedDataReload = ["calendar"]

  if (
    dataChangeType === "filterChange" &&
    widgetTypeThatDontNeedDataReload.includes(widgetInfo?.widgetType)
  )
    return false

  const hasFieldData = widgetInfo?.data?.length > 0

  return widgetInfo && hasFieldData
}

const removeContitionRecursively = conditionsObject => {
  if (conditionsObject?.filters === undefined) return {}
  if (Array.isArray(conditionsObject.filters) && conditionsObject.filters?.length === 0) return {}

  var index = conditionsObject.filters?.length - 1
  while (index >= 0) {
    if (
      conditionsObject.filters[index].hasOwnProperty("fieldUUID") &&
      conditionsObject.filters[index].fieldUUID === ""
    ) {
      conditionsObject.filters.splice(index, 1)
    } else if (conditionsObject.filters[index].hasOwnProperty("filters")) {
      if (conditionsObject.filters[index].filters?.length === 0) {
        conditionsObject.filters.splice(index, 1)
      } else {
        removeContitionRecursively(conditionsObject.filters[index])
      }
    }
    index -= 1
  }
  return conditionsObject
}

export const getNewWidgetwithoutLongNumber = widget => {
  var newData = []
  var newDependsOnField = []
  if (Array.isArray(widget.data)) {
    newData = widget.data.map(valueData => {
      return {
        ...valueData,
        conditions: getNewFilterWithoutLongNumber(valueData.conditions),
      }
    })
  }
  if (Array.isArray(widget.dependsOnField)) {
    newDependsOnField = widget.dependsOnField.map(valueData => {
      return {
        ...valueData,
        conditions: getNewFilterWithoutLongNumber(valueData.conditions),
      }
    })
  }
  return {
    ...widget,
    data: newData,
    ...(widget.dependsOnField !== undefined && {
      dependsOnField: newDependsOnField,
    }),
    ...(widget.conditions !== undefined && {
      conditions: getNewFilterWithoutLongNumber(widget.conditions),
    }),
  }
}

export const getNewFilterWithoutLongNumber = previousFilter => {
  var newFilter = JSON.parse(JSON.stringify(previousFilter))
  transformWidgetFilterToAvoidLongNumber(newFilter)
  return newFilter
}

export const transformWidgetFilterToAvoidLongNumber = filter => {
  if (filter === undefined) return

  if (Array.isArray(filter)) {
    filter.forEach(filterObj => {
      if (isObjectV2(filterObj.value) && filterObj.value.hasOwnProperty("$numberLong")) {
        filterObj.value = parseInt(filterObj.value.$numberLong)
      } else if (filterObj.hasOwnProperty("filters")) {
        transformWidgetFilterToAvoidLongNumber(filterObj.filters)
      }
    })
  } else {
    transformWidgetFilterToAvoidLongNumber(filter.filters)
  }
}

export const getConditionsWithValidUuid = conditions => {
  //this implementation is not perfect and may need improvement
  var firstResult = removeContitionRecursively(conditions)
  var secondResult = removeContitionRecursively(firstResult)
  var thirdResult = removeContitionRecursively(secondResult)
  return thirdResult
}

/**
 * Generates ETL request JSON from widget information.
 * @param widgetInfo - Object containing information about the widget.
 * @param pageFilter - Optional object containing page filter information.
 * @returns Object containing ETL request JSON.
 */
export const generateETLRequestJsonFromWidgetInfo = (
  widgetInfo,
  pageFilter = {},
  etlReqParams = {}
) => {
  const { widgetType } = widgetInfo
  // Check if widget type is dropdownList, cloudWords, or googleMap
  const isSearchBar = widgetType === "searchBar"
  const isDropdownList = widgetType === "dropdownList" || isSearchBar
  const isCloudWords = widgetType === "cloudWords"
  const isGoogleMap = widgetType === "googleMap"

  // Filter out temporary placeholder fields that haven't been assigned a real column yet
  const validDataBrute = widgetInfo.data.filter(
    f => f.id !== temporaryIdStr && f.uuid !== temporaryIdStr
  )

  // COLONNES DE MOIS : rebasées sur l'année regardée (écart E9).
  //
  // Les tableaux à 12 mois portent des conditions bornant chacune un mois d'une année FIGÉE à
  // l'installation — une condition compare une colonne à un littéral, elle ne sait pas extraire le
  // mois-de-l'année. Filtrer la page sur une autre année donnait douze colonnes vides, sans message.
  //
  // ⚠️ Ne touche QUE les colonnes qui se déclarent « colonne de mois » (`extra.monthOfYear`), et
  // seulement si un filtre de page porte sur leur axe. Tout autre widget passe ici sans être
  // modifié — cette fonction construit la requête de TOUS les widgets de la plateforme.
  const validData = rebaserColonnesDeMois(validDataBrute, pageFilter)

  // Create array of field IDs to select
  var selectRequest = validData.map(fullField => fullField.id)

  if (isGoogleMap && widgetInfo?.lattitude?.length && widgetInfo?.longitude?.length) {
    selectRequest.push(
      ...widgetInfo.lattitude.filter(f => f.id !== temporaryIdStr).map(field => field.id)
    )
    selectRequest.push(
      ...widgetInfo.longitude.filter(f => f.id !== temporaryIdStr).map(field => field.id)
    )
  }

  // Get category field ID if available
  var categoryFieldId = widgetInfo?.category?.id

  // Add groupBy fields to selectRequest if not already present (skip temp placeholders)
  widgetInfo.groupBy
    .filter(f => f.id !== temporaryIdStr && f.uuid !== temporaryIdStr)
    .forEach(groupByField => {
      var objectFound = selectRequest.find(uuidObj => uuidObj.uuid === groupByField.id)
      var fieldIsNotInSelect = !Boolean(objectFound)
      if (fieldIsNotInSelect) {
        selectRequest.push(groupByField.id)
      }
    })

  // Create array of groupBy field UUIDs (excluding temp placeholders)
  var groupByFieldUUIDs = widgetInfo.groupBy
    .filter(f => f.id !== temporaryIdStr && f.uuid !== temporaryIdStr)
    .map(fullField => fullField.id)

  // Add dependsOnField IDs to selectRequest if available (skip temp placeholders)
  if (widgetInfo.hasOwnProperty("dependsOnField")) {
    selectRequest.push(
      ...widgetInfo.dependsOnField.filter(f => f.id !== temporaryIdStr).map(field => field.id)
    )
  }

  // Create object to hold full request
  var fullRequestToReturn = {}

  // SELECT ADVANCED
  var advancedData = validData.map(fullField => {
    // Create copy of field conditions and get valid UUIDs
    var fullFieldConditions = fullField?.conditions
      ? JSON.parse(JSON.stringify(fullField?.conditions))
      : {}

    let finalFieldCondition = getConditionsWithValidUuid(fullFieldConditions)

    // Check if field conditions are available
    var fieldConditionAvailable =
      finalFieldCondition && Object.keys(finalFieldCondition)?.length > 0

    // Determine aggregation function to use
    var aggFunctionToUse = "auto"
    if (isCloudWords && fullField.calculus === "") aggFunctionToUse = ""
    else if (fullField?.calculus === undefined) aggFunctionToUse = "auto"
    else if (groupByFieldUUIDs?.length === 0) aggFunctionToUse = fullField.calculus
    else if (groupByFieldUUIDs?.length > 0 && fullField.calculus !== "")
      aggFunctionToUse = fullField.calculus

    // Check if field is distinct
    var distinct = Boolean(fullField?.distinct)

    // Set aggregation function to count if field is countDistinct
    if (fullField.calculus === "countDistinct") {
      aggFunctionToUse = "count"
      distinct = true
    }

    // Return object with field UUID, output UUID, aggregation function, distinct, and filters (if available)
    return {
      fieldUUID: fullField?.uuid ? fullField?.uuid : fullField?.id,
      outputUUID: fullField?.valueId ? fullField?.valueId : fullField?.id,
      ...(!isDropdownList && { aggFunction: aggFunctionToUse }),
      ...(!isDropdownList && { distinct: distinct }),
      ...(fieldConditionAvailable && { filters: finalFieldCondition }),
    }
  })

  if (isGoogleMap && widgetInfo?.lattitude?.length) {
    widgetInfo.lattitude
      .filter(f => f.id !== temporaryIdStr)
      .forEach(fullField => {
        advancedData.push({
          fieldUUID: fullField?.uuid ? fullField?.uuid : fullField?.id,
          outputUUID: fullField?.valueId ? fullField?.valueId : fullField?.id,
          aggFunction: "",
          distinct: false,
        })
      })
  }
  if (isGoogleMap && widgetInfo?.longitude?.length) {
    widgetInfo.longitude
      .filter(f => f.id !== temporaryIdStr)
      .forEach(fullField => {
        advancedData.push({
          fieldUUID: fullField?.uuid ? fullField?.uuid : fullField?.id,
          outputUUID: fullField?.valueId ? fullField?.valueId : fullField?.id,
          aggFunction: "",
          distinct: false,
        })
      })
  }
  // Add groupBy fields to advancedData if not already present (for counter widgets)
  widgetInfo.groupBy
    .filter(f => f.id !== temporaryIdStr && f.uuid !== temporaryIdStr)
    .forEach(groupbyElt => {
      // TICKET 4019 - 26/04/2022 -  problem if same value in field and in groupBy resolve in commente this ... >>>>>>
      var fieldIsNotInSelect = true
      if (widgetType === "counter") {
        var objectFound = advancedData.find(
          advancedValue => advancedValue.fieldUUID === groupbyElt.uuid
        )
        fieldIsNotInSelect = !Boolean(objectFound)
      }
      if (fieldIsNotInSelect) {
        advancedData.push({
          fieldUUID: groupbyElt?.uuid ? groupbyElt?.uuid : groupbyElt?.id,
          outputUUID: groupbyElt?.valueId ? groupbyElt?.valueId : groupbyElt?.id,
          aggFunction: "",
        })
      }
    })

  // Add dependsOnField data to advancedData if available
  if (widgetInfo.hasOwnProperty("dependsOnField")) {
    advancedData.push(
      ...widgetInfo.dependsOnField
        .filter(f => f.id !== temporaryIdStr)
        .map(field => {
          // Create copy of field conditions and get valid UUIDs
          var fieldConditions = field?.conditions
            ? JSON.parse(JSON.stringify(field?.conditions))
            : {}
          let finalFieldCondition = getConditionsWithValidUuid(fieldConditions)

          // Check if field conditions are available
          var fieldConditionAvailable =
            finalFieldCondition && Object.keys(finalFieldCondition)?.length > 0

          // Determine aggregation function to use
          var aggFunctionToUse = "auto"
          if (field?.calculus === undefined) aggFunctionToUse = "auto"
          else if (groupByFieldUUIDs?.length === 0) aggFunctionToUse = field.calculus
          else if (groupByFieldUUIDs?.length > 0 && field.calculus !== "")
            aggFunctionToUse = field.calculus

          // Return object with field UUID, output UUID, aggregation function, distinct, and filters (if available)
          return {
            fieldUUID: field?.uuid ? field?.uuid : field?.id,
            outputUUID: field?.valueId ? field?.valueId : field?.id,
            aggFunction: aggFunctionToUse,
            distinct: Boolean(field?.distinct),
            ...(fieldConditionAvailable && { filters: finalFieldCondition }),
          }
        })
    )
  }

  // Add advancedData to fullRequestToReturn
  fullRequestToReturn["selectAdvanced"] = [...advancedData]

  // Gantt : ajouter idSample pour identifier chaque ligne lors du drill-down.
  // Sans lui, dataChart n'a que des clés UUID de colonnes — impossible de filtrer
  // le DrillDown sur la ligne spécifique cliquée.
  // Si le champ primaire est un loaded_* (ex. dashboard PDC affichant des Contrôles),
  // on demande loaded_left_xxx.idSample (l'ID du Contrôle) plutôt que idSample (l'ID du PDC).
  if (widgetType === "ganttChartV2") {
    const primaryUuid = String(widgetInfo?.data?.[0]?.uuid || widgetInfo?.data?.[0]?.id || "")
    const isLinkedData = primaryUuid.startsWith("loaded_")
    const idSampleFieldUUID = isLinkedData
      ? `${primaryUuid.split(".").slice(0, -1).join(".")}.idSample`
      : "idSample"
    const hasIdSample = fullRequestToReturn.selectAdvanced.some(f => f.fieldUUID === idSampleFieldUUID)
    if (!hasIdSample) {
      fullRequestToReturn.selectAdvanced.push({ fieldUUID: idSampleFieldUUID, outputUUID: idSampleFieldUUID, aggFunction: "" })
    }
  }

  // GROUPBY - NEW VERSION
  if (widgetInfo.widgetType !== "counter") {
    // Add groupByFieldUUIDs to fullRequestToReturn if available
    if (groupByFieldUUIDs?.length > 0) {
      fullRequestToReturn["groupByFieldUUIDs"] = [...groupByFieldUUIDs]
    } else if (isDropdownList && validData.length) {
      /**
       * ⚠️ UNE liste déroulante porte UN champ — `getMaximumNumberFields` l'y contraint — mais une
       * BARRE DE RECHERCHE en porte plusieurs, et c'est tout son intérêt : chercher à la fois dans
       * le code d'un contrôle, son libellé et les personnes.
       *
       * Ne grouper que sur le premier revenait à ne jamais demander au moteur les valeurs des
       * autres : la barre ne pouvait trouver que le code. C'est le défaut que le client CRC a
       * formulé mot pour mot le 2026-09-22 — « il semble n'y avoir que le code qui fonctionne ».
       */
      const champsAInterroger = isSearchBar
        ? validData.map(f => f?.uuid || f?.id).filter(Boolean)
        : [validData[0]?.uuid || validData[0]?.id]
      fullRequestToReturn["groupByFieldUUIDs"] = champsAInterroger
    }

    // Add field frequency data to fullRequestToReturn if available
    var fieldFrequencyArr = getFieldFrequencyArr(widgetInfo)
    if (fieldFrequencyArr?.length > 0) {
      fullRequestToReturn["groupByFieldUUIDsFrequency"] = fieldFrequencyArr
    }

    // Binning numérique dynamique : un champ groupBy peut porter des bornes de tranches (`ranges`)
    // configurées côté widget (ex. [0, 0.5, 0.7, 0.9, 1]) → on les transmet au moteur ETL sous
    // `groupByFieldUUIDsRanges`, ce qui remplace le regroupement brut par un découpage en tranches.
    // Reconfigurable à tout moment (10 %, custom…) sans toucher aux données ni au schéma.
    var groupByFieldUUIDsRanges = widgetInfo.groupBy
      .filter(f => f.id !== temporaryIdStr && f.uuid !== temporaryIdStr)
      .filter(f => Array.isArray(f.ranges) && f.ranges.length >= 2)
      .map(f => [f.uuid || f.id, f.ranges.map(Number).filter(n => !Number.isNaN(n))])
      .filter(([, edges]) => edges.length >= 2)
    if (groupByFieldUUIDsRanges.length > 0) {
      fullRequestToReturn["groupByFieldUUIDsRanges"] = groupByFieldUUIDsRanges
    }
  }

  // Extract sorting information from widgetInfo and add to fullRequestToReturn
  var sortingTypeFromCatagory = widgetInfo?.category?.sortingType
  var sortObjFromRequestInfo = widgetInfo?.requestInformations?.sort
  var selectAdvanced = widgetInfo?.request?.selectAdvanced || []

  if (useBestWorstResults(sortObjFromRequestInfo)) {
    // On récupère la config de tri actuelle (souvent un fieldUUID)
    const sortUuidFromWidget = widgetInfo.requestInformations.sort.uuid
    // On essaie de retrouver la mesure correspondante dans selectAdvanced
    const sortMeasure = selectAdvanced.find(
      sa => sa.outputUUID === sortUuidFromWidget || sa.fieldUUID === sortUuidFromWidget
    )
    // Si on trouve une mesure, on privilégie son outputUUID, sinon on garde la valeur actuelle
    const uuidToSendForSort = sortMeasure?.outputUUID ?? sortUuidFromWidget

    fullRequestToReturn["sort"] = {
      fields: [uuidToSendForSort],
      ascending: isAscending(sortObjFromRequestInfo?.sortingType),
      useBestWorstResults: true,
    }
  } else if (categoryFieldId && sortingTypeFromCatagory && sortingTypeFromCatagory !== "default") {
    // Cas "classique" : tri sur un champ de catégorie → on continue d’envoyer le fieldUUID
    fullRequestToReturn["sort"] = {
      fields: [categoryFieldId],
      ascending: sortingTypeFromCatagory === "ascending",
    }
  }

  // Extract page filter information and add to fullRequestToReturn
  const hasPageFilter = pageFilter && Object.keys(pageFilter)?.length
  const transformedFilterFromPage = getTransformedFilterFromPage(pageFilter)
  const hasActiveFilterOnPage = transformedFilterFromPage?.length

  // Extract widget filter information and add to fullRequestToReturn
  var filterWidgetRequest = widgetInfo?.conditions
    ? JSON.parse(JSON.stringify(widgetInfo?.conditions))
    : {}
  let finalWidgetConditions = getConditionsWithValidUuid(filterWidgetRequest)
  finalWidgetConditions = getNewFilterWithoutLongNumber(finalWidgetConditions)

  const hasWidgetFilter = Boolean(
    finalWidgetConditions &&
    Object.keys(finalWidgetConditions)?.length &&
    finalWidgetConditions?.filters?.length
  )

  var filtersArray = []
  if (hasPageFilter && hasActiveFilterOnPage) {
    filtersArray = [...transformedFilterFromPage]
  }

  if (hasWidgetFilter) filtersArray.push({ ...finalWidgetConditions })

  if (filtersArray.length && widgetCanBeFiltered(widgetInfo)) {
    const operation = etlReqParams?.filtersBoolOperationType === "OR" ? "or" : "and"
    fullRequestToReturn["filter"] = {
      operation: operation,
      filters: filtersArray,
    }
  }

  // Add limit information to fullRequestToReturn if present in widgetInfo
  if (widgetInfo?.requestInformations?.limit) {
    fullRequestToReturn["limit"] = widgetInfo.requestInformations.limit
  }

  // Le pipeline `transform` de l'ETL est REPRIS TEL QUEL du document du widget.
  //
  // Cette fonction régénère intégralement le corps envoyé à `/etl/dashboard/:id/request` à partir
  // de `data` / `groupBy` / `conditions` : tout ce qu'elle ne recopie pas explicitement est perdu.
  // Un `transform` posé sur le widget était donc silencieusement jeté au rendu — le widget partait
  // sans son pipeline et l'ETL répondait sur des colonnes qui n'existaient pas.
  //
  // Il n'y a volontairement AUCUNE interface d'édition ici : ces pipelines sont écrits côté back,
  // le front ne fait que les transporter. Aucune validation non plus — la forme des étapes est
  // vérifiée par le compilateur de transform du back, qui refuse ce qu'il ne connaît pas ; la
  // dupliquer ici créerait deux vérités qui dérivent.
  if (Array.isArray(widgetInfo?.request?.transform) && widgetInfo.request.transform.length > 0) {
    fullRequestToReturn["transform"] = widgetInfo.request.transform
  }

  if (Array.isArray(widgetInfo?.window) && widgetInfo.window.length > 0) {
    const validWindow = widgetInfo.window.filter(
      f => f?.uuid !== temporaryIdStr && f?.id !== temporaryIdStr
    )
    const partitionBy = validWindow
      .filter(field => field?.category === "partitionBy")
      .map(({ uuid }) => ({ uuid }))

    if (partitionBy.length) {
      fullRequestToReturn.window = {
        ...fullRequestToReturn.window,
        partitionBy,
      }
    }

    const orderBy = validWindow
      .filter(field => field?.category === "orderBy")
      .map(({ uuid, ascending }) => ({ uuid, ascending }))

    if (orderBy.length) {
      fullRequestToReturn.window = {
        ...fullRequestToReturn.window,
        orderBy,
      }
    }
  }

  // Libellé combiné des options d'une liste déroulante (`widgetProps.optionLabel`) : les champs
  // d'affichage entrent dans la sélection et le groupement. Sans réglage, la requête est intacte.
  const requete = ajouterChampsLibelle(fullRequestToReturn, widgetInfo)

  // Source du widget : le cube du dashboard par défaut, ou une vue ETL déclarée sur la page.
  // Le back (composeViewRequest) compose la définition de la vue avec ce corps de requête :
  // le `filter` posé plus haut s'ajoute en ET au filtre propre de la vue. Ne pas le déplacer
  // dans `overrides`, où il écraserait ce filtre au lieu de le compléter.
  if (widgetInfo?.viewId) requete["viewId"] = widgetInfo.viewId

  return requete
}

/**
 * Éclate une cellule portant plusieurs valeurs jointes en autant de valeurs distinctes.
 *
 * Sans cela, un filtre sur un champ multivalué n'offre qu'une option par COMBINAISON rencontrée
 * — « Sami amriche, Maxime Croisier, Raph RG (PERSO) » en une seule entrée, au lieu de trois.
 *
 * ⚠️ Ne s'applique QUE si le widget déclare un séparateur. Un éclatement par défaut découperait
 * les libellés qui contiennent légitimement une virgule sur tous les dashboards existants.
 */
const eclaterValeursJointes = (valeurs, separateur) => {
  if (!separateur) return valeurs
  const sorties = []
  for (const v of valeurs) {
    if (typeof v !== "string") {
      sorties.push(v)
      continue
    }
    for (const morceau of v.split(separateur)) {
      const propre = morceau.trim()
      if (propre !== "") sorties.push(propre)
    }
  }
  return sorties
}

/**
 * Options d'un filtre, à plat (`unique`) et INDEXÉES PAR CHAMP (`byField`).
 *
 * ⚠️ `byField` est indexé par le **fieldUUID**, pas par la clé de sortie de la requête. Les deux
 * diffèrent : le moteur rend chaque colonne sous son `outputUUID`, qui est un identifiant tiré au
 * hasard à la construction du widget (`col()`, côté back). Le consommateur, lui, raisonne en
 * champs — c'est un `fieldUUID` qu'il faut mettre dans une condition de filtre. D'où `outputToField`,
 * fourni par l'appelant qui, seul, connaît la correspondance.
 *
 * ⚠️ TOUTES les clés d'une ligne sont lues, pas seulement la première. Une barre de recherche
 * multi-champs rend une ligne par COMBINAISON rencontrée — code, libellé, personnes — et n'en lire
 * qu'une colonne revenait à jeter les autres. C'est ce qui ne laissait fonctionner que le code.
 */
export const formatWidgetFilterBody = (
  widgetRequestBody,
  multiValueSeparator = null,
  outputToField = null,
  /**
   * Libellé combiné (`specLibelle`, `Utils/optionLabel`) : ses colonnes ne deviennent PAS des
   * options — sinon les codes seraient eux-mêmes sélectionnables — mais produisent `labels`,
   * `{ valeur: libellé }`, que la liste affiche à la place de la valeur.
   */
  optionLabel = null
) => {
  if (!Array.isArray(widgetRequestBody)) return { unique: [], byField: {} }

  const colonnesLibelle = sortiesLibelle(optionLabel)
  var uniqueValueList = []
  var byField = {}
  widgetRequestBody.forEach(item => {
    Object.keys(item ?? {}).forEach(outputKey => {
      if (colonnesLibelle.has(outputKey)) return
      const fieldKey = outputToField?.[outputKey] ?? outputKey
      const brutes = Array.isArray(item[outputKey]) ? item[outputKey] : [item[outputKey]]
      const fieldValues = eclaterValeursJointes(brutes, multiValueSeparator)
      uniqueValueList.push(...fieldValues)
      if (!byField[fieldKey]) byField[fieldKey] = []
      byField[fieldKey].push(...fieldValues)
    })
  })

  Object.keys(byField).forEach(k => {
    byField[k] = [...new Set(byField[k])]
  })

  return {
    unique: [...new Set(uniqueValueList)],
    byField,
    ...(optionLabel ? { labels: construireLibelles(widgetRequestBody, optionLabel) } : {}),
  }
}

export const useBestWorstResults = sortObject => {
  return !(
    sortObject === undefined ||
    sortObject?.uuid === undefined ||
    [undefined, sortingTypes.ALL_RESULT].includes(sortObject?.sortingType)
  )
}

export const isAscending = sortingType => {
  return sortingType === sortingTypes.WORST_RESULT
}

export const getFieldWithUuid = (fieldsData, uuid) => {
  var foundedField = fieldsData.find(field => field.uuid === uuid)
  if (foundedField) return foundedField
  return {}
}

export const getWidgetTraduction = (widgetType, langageId) => {
  const trad = getTranslation(langageId) || {}

  switch (widgetType) {
    case "mapping":
    case "mappingFilter":
      return {
        REGION: trad.REGION,
        DEPARTEMENT: trad.DEPARTEMENT,
        CLEAR_FILTER: trad?.CLEAR_FILTER,
      }
    case "redirectButton":
      return {
        NO_PAGE: trad.REDIRECT_BUTTON_NO_PAGE,
        NO_URL: trad.REDIRECT_BUTTON_NO_URL,
      }
    case "table":
      return {
        LINES: trad.LINES,
      }
    // Catégorie vide des graphiques à barres (cf. Utils/emptyCategory) — même libellé pour le drill-down.
    case "histogram":
    case "horizontalBarChart":
    case "stackedBar":
      return {
        EMPTY_CATEGORY: trad.NOT_SPECIFIED,
      }
    case "lastSampleWidget":
      return {
        SELECT_REFERENTIAL: trad.DASHBOARD_LAST_SAMPLE_SELECT_REFERENTIAL,
        EMPTY: trad.DASHBOARD_LAST_SAMPLE_EMPTY,
      }
    case "ganttChart":
      return {
        CURRENT_STEP: trad.DASHBOARD_GANTT_V1_CURRENT_STEP,
        TIMING: trad.DASHBOARD_GANTT_V1_TIMING,
        SEND_REMINDER: trad.DASHBOARD_GANTT_V1_SEND_REMINDER,
        FEATURE_IN_PROGRESS: trad.DASHBOARD_GANTT_V1_FEATURE_IN_PROGRESS,
      }
    // ⚠️ Sans ce cas, le widget reçoit `widgetTraduction === undefined` et retombe sur ses chaînes
    // anglaises de repli — sans erreur, sans log. Les widgets ne reçoivent PAS `trad` (le conteneur
    // ne passe que `widgetTraduction` et `languageId`), donc ajouter des clés aux locales ne suffit
    // pas : il faut aussi les exposer ICI, par type de widget.
    case "ganttChartV2":
      return {
        PICK_COLUMNS: trad.GANTT_PICK_COLUMNS,
        SKIPPED_ROWS: trad.GANTT_SKIPPED_ROWS,
        SKIPPED_ROWS_HINT: trad.GANTT_SKIPPED_ROWS_HINT,
        NO_END_ROLE: trad.GANTT_NO_END_ROLE,
        REVERSED_ROWS: trad.GANTT_REVERSED_ROWS,
        SAME_COLUMN: trad.GANTT_SAME_COLUMN,
        ALL_ZERO_LENGTH: trad.GANTT_ALL_ZERO_LENGTH,
      }
    case "dropdownList":
      return {
        SELECT_ALL: trad.SELECT_ALL,
        BOOL_TRUE: trad.BOOLEAN_INPUT_TRUE,
        BOOL_FALSE: trad.BOOLEAN_INPUT_FALSE,
        EMPTY_VALUE: trad.WIDGET_FILTER_EMPTY_VALUE,
      }
    case "hierarchicalFilter":
      return {
        LOADING: trad?.LOADING,
        EMPTY: trad?.WIDGET_FILTER_HIERARCHY_EMPTY,
        CONFIGURE: trad?.WIDGET_FILTER_HIERARCHY_CONFIGURE,
        PLACEHOLDER: trad?.WIDGET_FILTER_HIERARCHY_PLACEHOLDER,
        SELECTED: trad?.SELECTED,
        CLEAR: trad?.CLEAR_FILTER,
      }
    default:
      // Type inconnu (widget retiré du catalogue, enregistrement ancien) : le message de repli.
      return {
        UNKNOWN_TYPE: trad.DASHBOARD_WIDGET_UNKNOWN_TYPE,
      }
  }
}

export const isWidgetInFixedLayout = (page, widgetId) => {
  if (page?.fixedLayouts === undefined) return false
  var valueToReturn = false
  var breakpoints = Object.keys(page.fixedLayouts)
  breakpoints.forEach(breakpoint => {
    var indexFound = page.fixedLayouts[breakpoint].findIndex(
      widgetLayout => widgetLayout.i === widgetId
    )
    if (indexFound >= 0) valueToReturn = true
  })
  return valueToReturn
}

export const getNewWidgetDataAfterMoveFormTempToNewId = (
  actualWidgetData,
  dictionaryTempId_NewId
) => {
  var newWidgetData = actualWidgetData
  var tempWidgetIdList = Object.keys(dictionaryTempId_NewId)
  tempWidgetIdList.forEach(tempWidgetId => {
    var temporaryData = newWidgetData[tempWidgetId]
    var newWidgetId = dictionaryTempId_NewId[tempWidgetId]
    newWidgetData = {
      ...newWidgetData,
      [newWidgetId]: temporaryData,
    }
    var { [tempWidgetId]: theTempWidgetId, ...newWidgetsDataWithoutTempId } = newWidgetData
    newWidgetData = newWidgetsDataWithoutTempId
  })
  return newWidgetData
}

export const canBeParsedToInt = value => {
  try {
    var val = parseInt(value)
    if (isNaN(val)) return false
  } catch (e) {
    return false
  }
  return true
}

export const canBeParsedToFloat = value => {
  try {
    parseFloat(value)
  } catch (e) {
    return false
  }
  return true
}

export const onlyOneWidgetHasNotOwnProperty = (previousWidgetHasProperty, newWidgetHasProperty) => {
  return Boolean(previousWidgetHasProperty ^ newWidgetHasProperty)
}

export const itShouldQueryWidgetData = (previousWidgetInfos, newWidgetInfos) => {
  if (previousWidgetInfos === undefined || newWidgetInfos === undefined) {
    console.error("Custom error: Widget query data. A parameter is undefined")
    return false
  }
  const { data, groupBy, widgetType } = newWidgetInfos
  if (!Array.isArray(data) || !Array.isArray(groupBy) || !widgetType) return false

  const isPipeline = widgetType === "pipeline"
  if (isPipeline && (!data.length || !groupBy.length)) return false

  const propsToCheck = [
    "data",
    "groupBy",
    "category",
    "conditions",
    "requestInformations",
    "window",
  ]
  const widgetPropsToCheck = [] //add some if needed
  for (let props of propsToCheck) {
    var previousWidgetHasProperty = previousWidgetInfos.hasOwnProperty(props)
    var newWidgetHasProperty = newWidgetInfos.hasOwnProperty(props)
    if (onlyOneWidgetHasNotOwnProperty(previousWidgetHasProperty, newWidgetHasProperty)) return true
    else if (previousWidgetHasProperty && newWidgetHasProperty) {
      if (!isEqual(previousWidgetInfos[props], newWidgetInfos[props])) {
        return true
      }
    }
  }

  for (let props of widgetPropsToCheck) {
    if (
      previousWidgetInfos.hasOwnProperty(props) &&
      newWidgetInfos.hasOwnProperty(props) &&
      previousWidgetInfos.hasOwnProperty("widgetProps") &&
      newWidgetInfos.hasOwnProperty("widgetProps")
    ) {
      if (!isEqual(previousWidgetInfos.widgetProps[props], newWidgetInfos.widgetProps[props])) {
        return true
      }
    }
  }

  return false
}

export const getObjectToExport = (fieldsData, headerFields, datas, languageId, requestLoad) => {
  const header = getFieldsFromUUIDs(fieldsData, headerFields, languageId, requestLoad)
  const headerLabels = header.map(field => (field?.tags ? field?.tags?.label : field))
  const body = datas.map(row => Object.values(row))
  return { header: headerLabels, body }
}

export const addZeroBeforeNumber = number => {
  return number < 10 ? `0${number}` : `${number}`
}

export const timestampToDDMMYYYY = timestamp => {
  var newDate = new Date(timestamp)
  return [
    addZeroBeforeNumber(newDate.getDate()),
    addZeroBeforeNumber(newDate.getMonth() + 1),
    newDate.getFullYear(),
  ].join("/")
}

export const convertLetterMonthYearFormat = (date, languageId) => {
  const regexForDateFormat = /^((0[1-9])|(1[0-2]))\/(\d{4})$/g
  const isValidDateFormat = regexForDateFormat.test(date)
  if (isValidDateFormat && date.length === 7) {
    const [month, year] = date.split("/")
    const numberMonth = parseInt(month) - 1
    const monthName = getMonthNameWithMonthNumber(numberMonth, languageId)
    return `${monthName} ${year}`
  }

  return date
}

export const getFinalDateLabel = (timeValue, groupByFrequency, trad, languageId) => {
  const { DAY } = trad
  // Convertir un timestamp en format date si nécessaire
  let date = typeof timeValue === "number" ? moment(timeValue) : moment(timeValue, ["DD-MM-YYYY"])

  if (!date.isValid()) {
    return timeValue // Retourne la valeur brute si elle n'est pas valide
  }

  switch (groupByFrequency) {
    case undefined:
    case "default":
    case "dayMonthYear":
      return date.format("DD/MM/YYYY")

    case "day":
      return `${DAY} ${date.format("DD")}`

    case "month": {
      let monthNumber = date.format("MM") - 1
      return getMonthNameWithMonthNumber(monthNumber, languageId)
    }

    case "year":
      return date.format("YYYY")

    case "dayMonth":
      return date.format("DD/MM")

    case "monthYear":
      return date.format("MM/YYYY")

    // Le back regroupe déjà par DATE_TRUNC('quarter', …) : la date reçue est le 1er jour du
    // trimestre, dont on déduit le numéro. `Math.ceil(mois / 3)` — pas de table de correspondance.
    case "quarter":
      return `T${Math.ceil((date.month() + 1) / 3)}`

    case "quarterYear":
      return `T${Math.ceil((date.month() + 1) / 3)} ${date.format("YYYY")}`

    case "monthLetterYear":
      return convertLetterMonthYearFormat(date.format("MM/YYYY"), languageId, timeValue)

    default:
      return timeValue
  }
}

export const canUseVariationrate = widgetInfo => {
  const isAgTable = widgetInfo.widgetType === "ag_table"
  const customCalculusIsArray = Array.isArray(widgetInfo?.customCalculus)
  const hasCustomCalculus = customCalculusIsArray && widgetInfo?.customCalculus.length > 0
  const widgetHasAtLeast2Data = Array.isArray(widgetInfo.data) && widgetInfo.data.length >= 2
  // const widgetHasAtLeast1GroupBy = Array.isArray(widgetInfo.groupBy) && widgetInfo.groupBy.length >= 1

  return isAgTable && hasCustomCalculus && widgetHasAtLeast2Data
}

export const getVariationResult = (valueA, valueB) => {
  var newValueB = valueB
  if ([undefined, null, 0].includes(valueA)) return 1234567
  if ([undefined, null].includes(newValueB)) newValueB = 0

  if (typeof valueA !== "number" || typeof newValueB !== "number") return 0
  return (((newValueB - valueA) / valueA) * 100).toFixed(2)
}

export const getVariationResultAsStr = (valueA, valueB) => {
  let variationRate = getVariationResult(valueA, valueB)
  if (variationRate === 1234567) return "infinity"
  return `${variationRate}%`
}

// Ratio de deux colonnes exprimé en pourcentage : A/B × 100 (ex. « part réalisée » =
// nb réalisés / nb total). Renvoie 0 si le dénominateur est nul/absent.
export const getRatioResult = (valueA, valueB, decimals = 0) => {
  if ([undefined, null].includes(valueA) || [undefined, null, 0].includes(valueB)) return 0
  if (typeof valueA !== "number" || typeof valueB !== "number") return 0
  return Number(((valueA / valueB) * 100).toFixed(decimals))
}

export const getRatioResultAsStr = (valueA, valueB, decimals = 0) => {
  return `${getRatioResult(valueA, valueB, decimals)}%`
}

export const getTransformedVariationRateData = (previousData, widgetInfo) => {
  var calculusList = []
  if (Array.isArray(widgetInfo.customCalculus)) {
    calculusList = widgetInfo.customCalculus.filter(calculus => {
      return (
        calculus.valueIds.length === 2 &&
        (calculus.formula === "variationRate" ||
          calculus.formula === "ratio" ||
          calculus.formula === "ratioSum") &&
        calculus.variationId
      )
    })
  }

  var newData = previousData.map(dataRow => {
    let propertyToAdd = {}
    calculusList.forEach(calculus => {
      var variationValueId = calculus.variationId
      var [valueIdA, valueIdB] = calculus.valueIds
      if (calculus.formula === "ratio") {
        propertyToAdd[variationValueId] = getRatioResultAsStr(
          dataRow[valueIdA],
          dataRow[valueIdB],
          calculus.decimals ?? 0
        )
      } else if (calculus.formula === "ratioSum") {
        // A / (A + B) × 100 — ex. taux de remontée = validés / (validés + manqués)
        const a = Number(dataRow[valueIdA]) || 0
        const b = Number(dataRow[valueIdB]) || 0
        propertyToAdd[variationValueId] = getRatioResultAsStr(a, a + b, calculus.decimals ?? 0)
      } else {
        propertyToAdd[variationValueId] = getVariationResultAsStr(
          dataRow[valueIdA],
          dataRow[valueIdB]
        )
      }
    })
    return {
      ...dataRow,
      ...propertyToAdd,
    }
  })
  return newData
}

export const getTransformedDataHeatMapRisk = (widgetInfo, data, fieldsData) => {
  const colorData = widgetInfo?.widgetProps?.infosHeatMap?.colorValue || []
  const dataAxes = widgetInfo?.data || []

  const uuidToCategory = dataAxes.reduce((map, item) => {
    map[item.valueId] = item?.category
    return map
  }, {})
  const uuidToInfoDataSource = dataAxes?.reduce((field, item) => {
    field[item.valueId] = find(fieldsData, itemField => itemField?.uuid === item.uuid)?.dataSource
    return field
  }, {})

  const dataMap = {}

  // Traiter les données originales
  data?.forEach(record => {
    const transformedRecord = {}
    let nameUuid = null
    let idSample = null

    for (const key in record) {
      const category = uuidToCategory[key]
      // console.log("Category for key:", key, "is", category)

      if (category === "x" || category === "y") {
        if (typeof record[key] === "number") {
          record[key] = Math.trunc(record[key])
        }
      }

      if (!category) continue

      if (category === "name") {
        nameUuid = key
      }

      if (category === "id_redirect") {
        idSample = record[key]
      }

      // Si la catégorie existe déjà, on concatène avec " - "
      if (transformedRecord[category]) {
        transformedRecord[category] += " - " + record[key]
      } else {
        transformedRecord[category] = record[key]
      }
    }

    // Créer une clé unique pour regrouper par x et y
    const key = `${transformedRecord.x}-${transformedRecord.y}`

    // Si la clé existe déjà, incrémenter la valeur de "value", sinon initialiser à 1
    if (dataMap[key]) {
      dataMap[key].value += 1
      if (transformedRecord.name) {
        if (!Array.isArray(dataMap[key].name)) dataMap[key].name = []
        if (!Array.isArray(dataMap[key].infoModal)) dataMap[key].infoModal = []

        dataMap[key].name.push(transformedRecord.name)
        dataMap[key].infoModal.push({
          name: transformedRecord.name,
          info: uuidToInfoDataSource[nameUuid],
          nameUuid: nameUuid,
          idSample: idSample || "",
        })
      } else if (transformedRecord.name) {
        dataMap[key].name = [transformedRecord.name]
        dataMap[key].infoModal = [
          {
            name: transformedRecord.name,
            info: uuidToInfoDataSource[nameUuid],
            nameUuid: nameUuid,
            idSample: idSample || "",
          },
        ]
      }
    } else {
      dataMap[key] = {
        x: transformedRecord.x,
        y: transformedRecord.y,
        value: 1,
        ...(transformedRecord.name && {
          name: [transformedRecord.name],
          infoModal: [
            {
              name: transformedRecord.name,
              info: uuidToInfoDataSource[nameUuid],
              nameUuid: nameUuid,
              idSample: idSample || "",
            },
          ],
        }),
      }
    }
  })
  // S'assurer que toutes les combinaisons de colorData sont présentes dans le résultat final
  colorData.forEach(colorItem => {
    const key = `${colorItem.x}-${colorItem.y}`
    if (!dataMap[key]) {
      // Ajouter les combinaisons manquantes avec une valeur par défaut de 0
      dataMap[key] = {
        x: colorItem.x,
        y: colorItem.y,
        value: 0, // Valeur par défaut pour les entrées manquantes
      }
    }
  })

  // Générer les données finales avec les couleurs associées
  const dataFinal = Object.values(dataMap).map(item => {
    const colorItem = colorData?.find(
      color => toString(color.x) === toString(item.x) && toString(color.y) === toString(item.y)
    )
    return {
      ...item,
      columnSettings: { fill: colorItem ? colorItem.color : "#adadad" },
    }
  })

  return dataFinal
}

export const getTransformedData = (
  widgetInfo,
  data,
  fieldsData,
  partialTrad,
  options = { languageId: "1" }
) => {
  const { languageId } = options
  var dataToReturn = cloneDeep(data)
  if (widgetInfo.groupBy?.length === 0) return data

  var groupByUuid = widgetInfo?.groupBy?.[0]?.uuid || widgetInfo?.groupBy?.[0]?.id
  var groupByValueId = widgetInfo?.groupBy?.[0]?.valueId

  var groupUuidField = fieldsData.find(field => field.uuid === groupByUuid)
  if (groupUuidField === undefined) return data

  //add seperator aand extract value from array when array_agg
  var listOfValueIdWithArrayAgg = []
  widgetInfo.data.forEach(valueData => {
    if (valueData.calculus === "array_agg") {
      listOfValueIdWithArrayAgg.push(valueData.valueId)
    }
  })

  if (listOfValueIdWithArrayAgg?.length) {
    dataToReturn.forEach((dataRow, index) => {
      Object.keys(dataRow).forEach(key => {
        if (listOfValueIdWithArrayAgg.includes(key) && Array.isArray(dataRow[key])) {
          var separator = "; "
          var newDataRow = dataRow[key].join(separator)
          dataToReturn[index][key] = newDataRow
        }
      })
    })
  }

  //modify date display
  if (groupUuidField?.dataType === "Date" && data?.length > 0) {
    var embeddedData = data.map(dataLine => {
      var timeValue = dataLine[groupByValueId]
      var frequency = widgetInfo.groupBy[0]?.frequency

      return {
        ...dataLine,
        [groupByValueId]: getFinalDateLabel(timeValue, frequency, partialTrad, languageId),
      }
    })
    dataToReturn = embeddedData
  }

  if (widgetInfo.widgetType === "pieChart") {
    dataToReturn.forEach((dataRow, index) => {
      Object.keys(dataRow).forEach(key => {
        if (dataRow[key] === null) {
          dataToReturn[index][key] = 0
        }
      })
    })
  }

  //modifyData when widget has variation rate
  if (canUseVariationrate(widgetInfo)) {
    dataToReturn = getTransformedVariationRateData(dataToReturn, widgetInfo)
  }

  if (widgetInfo?.widgetProps?.valueMapper?.length > 0) {
    dataToReturn = getTransformedValueMapperData(dataToReturn, widgetInfo)
    if (widgetInfo?.widgetProps?.mergeRenamedCategories === true) {
      dataToReturn = fusionnerCategoriesRenommees(dataToReturn, widgetInfo)
    }
  }

  return dataToReturn
}

/**
 * Fusionne les lignes dont la catégorie, une fois renommée par `valueMapper`, est la même — en
 * SOMMANT leurs mesures. Opt-in par `widgetProps.mergeRenamedCategories`.
 *
 * Sans elle, deux valeurs d'origine renommées pareil donnent deux barres portant le même libellé.
 * Cas d'origine : « À répondre » et « En cours » en une barre « En cours » (dashboard Contrôle V2,
 * recette CRC du 13/10/2026), pour que la barre égale la vignette « Évaluations en cours ».
 *
 * ⚠️ La SOMME n'est juste que pour des comptages de catégories disjointes : la fabrique back refuse
 * le réglage sur une mesure. Le clic sur la barre fusionnée est traité à part (`containers/Widget`,
 * `reverseMapCategory`) : il filtre sur TOUTES les valeurs d'origine.
 *
 * L'ordre est celui de la première apparition de chaque catégorie ; les autres colonnes de la ligne
 * (bornes brutes, etc.) sont celles de cette première ligne.
 */
/**
 * Filtre du drill-down d'une barre FUSIONNÉE : un OU sur toutes les valeurs d'origine que
 * `valueMapper` renomme comme la catégorie cliquée. `null` hors fusion, ou si la catégorie ne
 * regroupe qu'une valeur — l'appelant garde alors son filtre `==` habituel.
 *
 * ⚠️ Un `==` sur la première valeur seule listerait « À répondre » sans « En cours » : un détail qui
 * ne compte pas le même nombre de lignes que la barre cliquée, sans aucune erreur.
 */
export const filtreCategorieFusionnee = (widgetProps, categoryValue, fieldUUID) => {
  if (widgetProps?.mergeRenamedCategories !== true || categoryValue == null) return null
  const origines = (widgetProps?.valueMapper ?? [])
    .filter(m => m?.renamedValue === String(categoryValue))
    .map(m => m.originalValue)
  if (origines.length < 2) return null
  return {
    operation: "or",
    filters: origines.map(o =>
      o !== "" && o != null ? { fieldUUID, operation: "==", value: o } : { fieldUUID, operation: "IS NULL" }
    ),
  }
}

export const fusionnerCategoriesRenommees = (data, widgetInfo) => {
  const cle = widgetInfo?.groupBy?.[0]?.valueId
  const mesures = (widgetInfo?.data ?? []).map(d => d?.valueId).filter(Boolean)
  if (!cle || !Array.isArray(data) || mesures.length === 0) return data

  const parCategorie = new Map()
  for (const ligne of data) {
    const categorie = ligne?.[cle]
    const deja = parCategorie.get(categorie)
    if (!deja) {
      parCategorie.set(categorie, { ...ligne })
      continue
    }
    for (const m of mesures) deja[m] = toNumber(deja[m], 0) + toNumber(ligne?.[m], 0)
  }
  return Array.from(parCategorie.values())
}

/**
 * Builds stacked-bar props that put the YEAR on the X axis and split each year
 * into MONTH series, from a single date-grouped histogram.
 *
 * Relies on the monthly-resolution rows the ETL already returns (frequency
 * "monthYear"/"month"): every row carries one month-of-a-year bucket + a measure.
 * We re-split each bucket into (year, month) and pivot months into series columns.
 * Returns null when the data can't be pivoted (no date/measure, unparseable dates)
 * so callers fall back to the normal rendering.
 */
export const getTemporalStackProps = (widgetInfo, rawData, languageId = "1") => {
  if (!Array.isArray(rawData) || rawData.length === 0) return null
  const dateValueId = widgetInfo?.groupBy?.[0]?.valueId
  const measureValueId = widgetInfo?.data?.[0]?.valueId
  if (!dateValueId || !measureValueId) return null

  const YEAR_KEY = "__year"
  const monthKey = idx => `m${idx}`

  // year -> { monthIdx -> summed measure }
  const pivot = {}
  const monthsPresent = new Set()

  rawData.forEach(row => {
    const rawDate = row[dateValueId]
    const date =
      typeof rawDate === "number"
        ? moment(rawDate)
        : moment(rawDate, [
            "DD/MM/YYYY",
            "DD/MM/YYYY HH:mm",
            "DD-MM-YYYY",
            "YYYY-MM-DD",
            moment.ISO_8601,
          ])
    if (!date.isValid()) return
    const year = String(date.year())
    const monthIdx = date.month() // 0-11
    const value = toNumber(row[measureValueId], 0)
    if (!pivot[year]) pivot[year] = {}
    pivot[year][monthIdx] = (pivot[year][monthIdx] || 0) + value
    monthsPresent.add(monthIdx)
  })

  const years = Object.keys(pivot).sort()
  if (years.length === 0) return null
  const monthIdxs = Array.from(monthsPresent).sort((a, b) => a - b)

  const dataChart = years.map(year => {
    const r = { [YEAR_KEY]: year }
    monthIdxs.forEach(idx => {
      r[monthKey(idx)] = pivot[year][idx] || 0
    })
    return r
  })

  return {
    dataChart,
    categoryData: YEAR_KEY,
    valueData: monthIdxs.map(monthKey),
    dataNames: monthIdxs.map(idx => getMonthNameWithMonthNumber(idx, languageId)),
    typeBarChart: "stackedBar",
    // Force the legend on so each colour is labelled with its month.
    visibilityLegend: true,
    legendPosition: "right",
  }
}

export const getTransformedValueMapperData = (data, widgetInfo) => {
  const valueMapper = widgetInfo?.widgetProps?.valueMapper || []

  return data.map(dataRow => {
    const transformedRow = { ...dataRow }
    valueMapper.forEach(({ originalValue, renamedValue }) => {
      Object.keys(transformedRow).forEach(key => {
        if (transformedRow[key] === originalValue) {
          transformedRow[key] = renamedValue
        }
      })
    })

    return transformedRow
  })
}

export const getColumnsLabelsMapping = (columns = []) => {
  const columnsLabelsMapping = columns.map(column => {
    var label = ""

    if (column?.valueDataName) label = column.valueDataName
    else if (column?.name) label = column.name
    else if (column?.tags?.label) label = column.tags.label

    var usePercent = column?.calculus === "overallPercentage"
    return {
      label: label,
      uuid: column?.uuid,
      valueId: column.valueId,
      type: column?.dataType || "",
      displayPercentSymbolAfterValue: usePercent,
      // Ratio 0–1 à afficher en % (×100) — porté par l'item de données du widget
      displayAsPercent: Boolean(column?.displayAsPercent),
      // Seuils de couleur de cellule (heatmap), portés par l'item de données du widget :
      // `[{ operator, value, color }]` — l'ancien format `{ over, color }` reste lu par
      // `getThresholdCellStyle` (interprété comme `> over`) pour les widgets déjà configurés.
      colorThresholds: Array.isArray(column?.colorThresholds) ? column.colorThresholds : undefined,
      customSortOrder: column?.customSortOrder,
      customSortEnabled: column?.customSortEnabled,
    }
  })
  return columnsLabelsMapping
}

export const mapDataNames = (widgetInfo = {}) => {
  const dataNames = []

  const addToDataNames = field => dataNames.push({ name: field.name, valueId: field.valueId })

  if (widgetInfo.hasOwnProperty("groupBy") && widgetInfo?.groupBy.length > 0) {
    widgetInfo.groupBy.forEach(field => addToDataNames(field))
  }

  if (widgetInfo.hasOwnProperty("window") && widgetInfo?.window?.length > 0) {
    widgetInfo.window.forEach(field => addToDataNames(field))
  }

  if (widgetInfo.hasOwnProperty("data") && widgetInfo?.data.length > 0) {
    widgetInfo.data.forEach(field => addToDataNames(field))
  }

  if (widgetInfo.hasOwnProperty("dependsOnField") && widgetInfo?.dependsOnField.length > 0) {
    widgetInfo.dependsOnField.forEach(field => addToDataNames(field))
  }

  return dataNames
}

export const getFillingChartData = (widgetInfo, dataArgument) => {
  if (!Array.isArray(widgetInfo?.data) || !Array.isArray(widgetInfo?.dependsOnField)) return []
  var dataValueId = ""
  dataValueId = widgetInfo?.data?.[0]?.valueId
  var dependsOnValueId = ""
  dependsOnValueId = widgetInfo?.dependsOnField?.[0]?.valueId
  let fillingGaugeChartData = dataArgument?.specificWidgetData?.data
  if (!Array.isArray(fillingGaugeChartData) || fillingGaugeChartData.length === 0) return []
  if (
    fillingGaugeChartData[0]?.[dataValueId] === undefined ||
    fillingGaugeChartData[0]?.[dependsOnValueId] === undefined
  ) {
    return []
  }

  var firstValue = fillingGaugeChartData[0][dataValueId]
  var secondValue = fillingGaugeChartData[0][dependsOnValueId]

  if (typeof firstValue !== "number" || typeof secondValue !== "number") return []

  var percentageCalculated = 100
  if (secondValue !== 0) percentageCalculated = (firstValue / secondValue) * 100
  if (percentageCalculated >= 100) percentageCalculated = 100

  var truncatePercentage = Math.trunc(percentageCalculated)
  var restPercent = 100 - truncatePercentage
  // For hide label based on text label
  const defaultLabel = "defaultLabel5699853645672345634"

  var categoryData = widgetInfo?.data[0]?.name

  var dataChart = [
    {
      value: restPercent,
      category: defaultLabel,
    },
    {
      value: truncatePercentage,
      category: `${categoryData}`,
    },
  ]
  return dataChart
}

/** Inject props to widget in function of several parameters */
export const getDataRelatedProps = params => {
  const {
    widgetInfo,
    dataArgument,
    filterActions,
    othersActions,
    fieldsData,
    partialTrad,
    dataOptions,
    widgetHasNoData,
  } = params

  var dataRelatedProps = {}
  const widgetType = widgetInfo?.widgetType || ""
  const widgetId = widgetInfo?._id?.$oid
  const isFilter = widgetInfo?.widgetCategory === widgetCategory.FILTER
  const isRedirectButton = widgetType === "redirectButton"
  const isGaugeChart = widgetType === "gaugeChart"
  const isTable = [
    "table",
    "ag_table",
    "ganttChart",
    "ganttChartV2",
    "googleMap",
    "caseCardMSF",
  ].includes(widgetType)
  const isCalendar = widgetType === "calendar"
  const isTreeMap = widgetType === "treeMap"
  const isProgressBar = widgetType === "progressBar"
  const isMultiGauge = widgetType === "multiGauge"
  const isBubbleChart = widgetType === "bubbleChart"
  const isHeatMap = widgetType === "heatMap"
  const isHeatMapRisk = widgetType === "heatMapRisk"
  const isCloudWords = widgetType === "cloudWords"
  const isFillingGauge = widgetType === "fillingGauge"
  const isEvolutionWidget = widgetType === "evolutionWidget"
  const isPipeline = widgetType === "pipeline"

  dataRelatedProps["displayModeValue"] = getScaleAndEnumColors(widgetInfo?.data, fieldsData)

  dataRelatedProps["comparisonValues"] = dataArgument?.comparisonValues
    ? dataArgument.comparisonValues
    : {}

  if (isFilter || isRedirectButton) {
    var value = dataArgument.valueFilterSelected?.[widgetId]?.value
    if (value != null) dataRelatedProps["value"] = Array.isArray(value) ? value : [value]
    dataRelatedProps["data"] = dataArgument.specificWidgetFilterData
    dataRelatedProps["onSelectFilterValue"] = obj =>
      filterActions.onSelectFilterValueWrapperFunc(obj, widgetId)
    dataRelatedProps["onRemoveSelectedValue"] = () =>
      filterActions.onRemoveSelectedValueFunc(widgetId)
    if (widgetType === "searchBar" && widgetInfo?.data?.length > 1) {
      dataRelatedProps["filterFields"] = widgetInfo.data.map(({ id }) => id)
    }
    if (isCalendar) {
      dataRelatedProps["onSelectComparisonValue"] = obj =>
        filterActions.onSelectComparisonValueWrapperFunc(obj, widgetId)
      dataRelatedProps["onRemoveComparisonValue"] = () =>
        filterActions.onRemoveComparisonValue(widgetId)
    }
    if (widgetType === "rangeFilter") {
      dataRelatedProps["rangeValue"] = dataArgument.valueFilterSelected?.[widgetId]?.rangeValue
      dataRelatedProps["affectedField"] = widgetInfo?.groupBy.map(({ valueId }) => valueId)
      dataRelatedProps["dataField"] = widgetInfo?.data.map(({ valueId }) => valueId)
      const rangeFilterData = dataArgument?.specificWidgetData?.data
      if (rangeFilterData) {
        dataRelatedProps["rawData"] = getTransformedData(
          widgetInfo,
          rangeFilterData,
          fieldsData,
          partialTrad,
          dataOptions
        )
      }
    }
    if (widgetType === "hierarchicalFilter") {
      const fieldUuid = getFirstWidgetUUID(widgetInfo)
      const field = Array.isArray(fieldsData)
        ? fieldsData.find(f => f.uuid === fieldUuid)
        : undefined
      dataRelatedProps["referentialId"] = field?.dataSource?.referentialId
      dataRelatedProps["relationId"] = widgetInfo?.widgetProps?.relationId
      dataRelatedProps["parentSide"] = widgetInfo?.widgetProps?.parentSide || "left"
      dataRelatedProps["labelColumnId"] = widgetInfo?.widgetProps?.labelColumnId
      dataRelatedProps["displayMode"] = widgetInfo?.widgetProps?.displayMode || "dropdown"
    }
  }
  // else if (isGaugeChart &&dataArgument.valueData !== "") {
  //   let gaugeChartData = dataArgument?.specificWidgetData?.data
  //   if (gaugeChartData) {
  //     var dataFieldContainingValue = widgetInfo.data.find(dataField => dataField.calculus !== "")
  //     var dataValueId = dataFieldContainingValue?.valueId
  //     var newDataChart = undefined
  //     if (dataValueId) {
  //       newDataChart = gaugeChartData.map(dataRow => {
  //         let rowToReturn = { value: 0, name: "" }
  //         for (const [key, value] of Object.entries(dataRow)) {
  //           if (key === dataValueId) {
  //             rowToReturn.value = value
  //           } else rowToReturn.name = value
  //         }
  //         return rowToReturn
  //       })
  //     }
  //     dataRelatedProps["dataChart"] = newDataChart
  //   }
  //   dataRelatedProps["valueData"] = dataArgument.valueData
  // }
  else if (isGaugeChart && dataArgument.valueData !== "") {
    let gaugeChartData = dataArgument?.specificWidgetData?.data
    if (gaugeChartData) {
      var dataFieldContainingValue = widgetInfo.data[0]
      var dataValueId = dataFieldContainingValue?.valueId
      var newDataChart = undefined
      if (dataValueId) {
        newDataChart = gaugeChartData.map(dataRow => {
          let rowToReturn = { value: 0, name: "" }
          for (const [key, value] of Object.entries(dataRow)) {
            if (key === dataValueId) {
              rowToReturn.value = toNumber(value, 0)
              rowToReturn.name = dataFieldContainingValue?.name
              rowToReturn.id = key
            } else {
              rowToReturn.name = Number(value)
              rowToReturn.id = key
            }
          }
          return rowToReturn
        })
      }
      dataRelatedProps["dataChart"] = newDataChart
    }
    dataRelatedProps["valueData"] = dataArgument.valueData
  } else if (isFillingGauge) {
    dataRelatedProps["dataChart"] = getFillingChartData(widgetInfo, dataArgument)
    //var dataPercent = fillingGaugeChartData[0].
  } else if (isCloudWords && dataArgument.valueData !== "") {
    let cloudWordData = dataArgument?.specificWidgetData?.data
    if (cloudWordData) {
      var dataFieldContainingValue = widgetInfo.data.find(dataField => dataField.calculus !== "")
      var dataFieldContainingNames = widgetInfo.data.find(dataField => dataField.calculus === "")
      dataRelatedProps["dataChart"] = cloudWordData
      dataRelatedProps["valueData"] = dataFieldContainingValue?.valueId
      dataRelatedProps["categoryData"] = dataFieldContainingNames?.valueId
    }
  } else if (isTable && dataArgument.valueData !== "") {
    var tableData = dataArgument.specificWidgetData.data
    if (tableData?.length) {
      var newData = getTransformedData(widgetInfo, tableData, fieldsData, partialTrad, dataOptions)
      dataRelatedProps["dataChart"] = newData
    }
    dataRelatedProps["valueData"] = dataArgument.valueData
    const columns = []

    widgetInfo.groupBy.forEach(groupByField => {
      let field = getFieldFromUUID(fieldsData, groupByField.uuid)
      columns.push({
        ...field,
        name: groupByField.name,
        valueId: groupByField.valueId,
        // Une dimension peut aussi porter une mise en forme par seuils : à la maille d'un plan, son
        // taux est une valeur unique par ligne, posée en dimension pour choisir sa place — le
        // tableau affiche toujours les dimensions AVANT les mesures.
        colorThresholds: groupByField?.colorThresholds,
      })
    })

    widgetInfo.data.forEach(valueDataTemp => {
      let field = getFieldFromUUID(fieldsData, valueDataTemp.uuid)
      columns.push({
        ...field,
        name: valueDataTemp.name,
        valueId: valueDataTemp.valueId,
        calculus: valueDataTemp?.calculus,
        displayAsPercent: valueDataTemp?.displayAsPercent,
        colorThresholds: valueDataTemp?.colorThresholds,
        customSortOrder: valueDataTemp?.customSortOrder,
        customSortEnabled: valueDataTemp?.customSortEnabled,
      })
    })

    if (Array.isArray(widgetInfo.customCalculus)) {
      widgetInfo.customCalculus.forEach(calculus => {
        if (
          Array.isArray(calculus.valueIds) &&
          calculus.valueIds.length === 2 &&
          calculus.valueIds[0] !== "" &&
          calculus.valueIds[1] !== ""
        ) {
          columns.push({
            name: calculus.name || "variation",
            valueId: calculus.variationId,
          })
        }
      })
    }

    const columnsLabelsMapping = getColumnsLabelsMapping(columns)
    dataRelatedProps["columnsLabelsMapping"] = columnsLabelsMapping
    dataRelatedProps["parentId"] = `chartContainer${widgetId}`

    if (widgetType === "googleMap") {
      const allFields = [...(widgetInfo.groupBy || []), ...(widgetInfo.data || [])]
      const fieldWithRefId = allFields.find(f => {
        const field = getFieldFromUUID(fieldsData, f.uuid)
        return field?.dataSource?.referentialId
      })
      if (fieldWithRefId) {
        const field = getFieldFromUUID(fieldsData, fieldWithRefId.uuid)
        dataRelatedProps["referentialId"] = field.dataSource.referentialId
      }
    }
  } else if (isTreeMap && dataArgument.valueData !== "") {
    var treeMapData = dataArgument?.specificWidgetData?.data
    if (treeMapData) {
      dataRelatedProps["dataChart"] = getTransformedData(
        widgetInfo,
        treeMapData,
        fieldsData,
        partialTrad,
        dataOptions
      )
    }
    dataRelatedProps["valueData"] = dataArgument.valueData
    dataRelatedProps["categoryData"] = widgetInfo?.groupBy.map(({ valueId }) => valueId)
  } else if (isMultiGauge && dataArgument.valueData !== "") {
    var multiGaugeData = dataArgument?.specificWidgetData?.data
    if (multiGaugeData) {
      dataRelatedProps["dataChart"] = getTransformedData(
        widgetInfo,
        multiGaugeData,
        fieldsData,
        partialTrad,
        dataOptions
      )
    }
    dataRelatedProps["valueData"] = dataArgument.valueData
    dataRelatedProps["categoryData"] = dataArgument.categoryData
    if (widgetInfo?.dependsOnField) {
      dataRelatedProps["dependsOnField"] = widgetInfo.dependsOnField[0]?.valueId
    }
  } else if (isBubbleChart && dataArgument.valueData !== "") {
    var widgetData = dataArgument?.specificWidgetData?.data
    if (widgetData) {
      dataRelatedProps["dataChart"] = getTransformedData(
        widgetInfo,
        widgetData,
        fieldsData,
        partialTrad,
        dataOptions
      )
    }

    const categoryXItem = widgetInfo?.data.find(item => item.category === "x")
    const categoryYItem = widgetInfo?.data.find(item => item.category === "y")
    const categoryValueItem = widgetInfo?.data.find(item => item.category === "value")
    const categoryNameItem = widgetInfo?.data.find(item => item.category === "name")

    const categoryPartitionByItem = widgetInfo?.window.find(
      item => item?.category === "partitionBy"
    )
    const categoryOrderByItem = widgetInfo?.window.find(item => item?.category === "orderBy")
    const categoryFrameClause = widgetInfo?.window.find(item => item?.category === "frameClause")

    dataRelatedProps["categoryXId"] = categoryXItem?.valueId
    dataRelatedProps["categoryYId"] = categoryYItem?.valueId
    dataRelatedProps["categoryValueId"] = categoryValueItem?.valueId
    dataRelatedProps["categoryNameId"] = categoryNameItem?.valueId
    dataRelatedProps["categoryPartitionById"] = categoryPartitionByItem?.valueId
    dataRelatedProps["categoryOrderById"] = categoryOrderByItem?.valueId
    dataRelatedProps["categoryFrameClauseId"] = categoryFrameClause?.valueId
  } else if (isEvolutionWidget && dataArgument.valueData !== "") {
    const evolutionData = dataArgument?.specificWidgetData?.data

    if (evolutionData) {
      dataRelatedProps["dataChart"] = getTransformedData(
        widgetInfo,
        evolutionData,
        fieldsData,
        partialTrad,
        dataOptions
      )
    }
    dataRelatedProps["valueData"] = dataArgument.valueData
    if (widgetInfo?.dependsOnField) {
      dataRelatedProps["dependsOnField"] = widgetInfo.dependsOnField[0]?.valueId
    }
  } else if (isHeatMap && dataArgument.valueData !== "") {
    var heatMapData = dataArgument?.specificWidgetData?.data
    if (heatMapData) {
      dataRelatedProps["dataChart"] = getTransformedData(
        widgetInfo,
        heatMapData,
        fieldsData,
        partialTrad,
        dataOptions
      )
    }
    const categoryXItem = widgetInfo?.groupBy.find(item => item.category === "x_groupBy")
    const categoryYItem = widgetInfo?.groupBy.find(item => item.category === "y_groupBy")
    const categoryValueItem = widgetInfo?.data.find(item => item.category === "value")

    //TODO: Remove this shitty condition after 1 month waiting for conversion : 03/06/2022
    if (categoryValueItem === undefined && widgetInfo?.data.length >= 1) {
      dataRelatedProps["categoryValueId"] = widgetInfo?.data[0]?.valueId
    } else {
      dataRelatedProps["categoryValueId"] = categoryValueItem?.valueId
    }
    if (
      categoryXItem === undefined &&
      categoryYItem === undefined &&
      widgetInfo?.groupBy.length == 2
    ) {
      dataRelatedProps["categoryXId"] = widgetInfo?.groupBy[0]?.valueId
      dataRelatedProps["categoryYId"] = widgetInfo?.groupBy[1]?.valueId
    } else {
      dataRelatedProps["categoryXId"] = categoryXItem?.valueId
      dataRelatedProps["categoryYId"] = categoryYItem?.valueId
    }
  } else if (isHeatMapRisk && dataArgument.valueData !== "") {
    var heatMapRiskData = dataArgument?.specificWidgetData?.data
    var heatMapRiskInfos = widgetInfo?.widgetProps?.infosHeatMap

    dataRelatedProps["xAxisValue"] = heatMapRiskInfos?.xAxisValue
    dataRelatedProps["yAxisValue"] = heatMapRiskInfos?.yAxisValue
    dataRelatedProps["dataChart"] = getTransformedDataHeatMapRisk(
      widgetInfo,
      heatMapRiskData,
      fieldsData
    )
  } else if (isProgressBar && dataArgument.valueData !== "") {
    var widgetData = dataArgument?.specificWidgetData?.data
    if (widgetData) {
      dataRelatedProps["dataChart"] = getTransformedData(
        widgetInfo,
        widgetData,
        fieldsData,
        partialTrad,
        dataOptions
      )
    }
    dataRelatedProps["valueData"] = dataArgument.valueData
    dataRelatedProps["categoryData"] =
      widgetInfo?.groupBy.length > 1
        ? widgetInfo?.groupBy.map(({ valueId }) => valueId)
        : dataArgument.categoryData
    dataRelatedProps["forceDisplayPercentSymbol"] = aWidgetValueDataHasOverallPercentage(widgetInfo)

    dataRelatedProps["dataExportFunction"] = (
      fileType = "",
      title = "",
      valueData = [],
      dataChart = []
    ) => {
      const listOfFieldsUUID = getListOfFieldsUUID(valueData, widgetInfo)
      const objectToExport = getObjectToExport(fieldsData, listOfFieldsUUID, dataChart)
      exportDatas(objectToExport, fileType, title)
    }
  } else if (isPipeline && dataArgument.valueData !== "") {
    const widgetData = dataArgument?.specificWidgetData?.data
    if (widgetData) {
      dataRelatedProps["dataChart"] = getTransformedData(
        widgetInfo,
        widgetData,
        fieldsData,
        partialTrad,
        dataOptions
      )
    }
    dataRelatedProps["valueData"] = dataArgument.valueData
    dataRelatedProps["categoryData"] =
      widgetInfo?.groupBy.length > 1
        ? widgetInfo?.groupBy.map(({ valueId }) => valueId)
        : dataArgument.categoryData
    const pipelineColumns = widgetInfo?.widgetProps?.pipelineColumns
    if (Array.isArray(pipelineColumns) && pipelineColumns.length) {
      dataRelatedProps["pipelineColumns"] = pipelineColumns
    }
    dataRelatedProps["onMoveElements"] = moves => {
      const payloads = getPayloadsToUpdateDataSource(widgetInfo, fieldsData, moves)
      for (let payload of payloads) {
        othersActions?.updateDataSource(payload)
      }
    }
  } else if (dataArgument.valueData !== "" && dataArgument.categoryData !== "") {
    var widgetData = dataArgument?.specificWidgetData?.data
    if (widgetData) {
      dataRelatedProps["dataChart"] = getTransformedData(
        widgetInfo,
        widgetData,
        fieldsData,
        partialTrad,
        dataOptions
      )
    }

    dataRelatedProps["valueData"] = dataArgument.valueData
    dataRelatedProps["categoryData"] =
      widgetInfo?.groupBy.length > 1
        ? widgetInfo?.groupBy.map(({ valueId }) => valueId)
        : dataArgument.categoryData
    dataRelatedProps["forceDisplayPercentSymbol"] = aWidgetValueDataHasOverallPercentage(widgetInfo)

    // Temporal stack (opt-in): pivot monthly rows into year-on-X / month-as-series.
    if (widgetInfo?.widgetProps?.temporalStack?.enabled && barChartList.includes(widgetType)) {
      const rawTemporalData = dataArgument?.specificWidgetData?.data
      const stackProps = getTemporalStackProps(widgetInfo, rawTemporalData, dataOptions?.languageId)
      if (stackProps) {
        dataRelatedProps["dataChart"] = stackProps.dataChart
        dataRelatedProps["categoryData"] = stackProps.categoryData
        dataRelatedProps["valueData"] = stackProps.valueData
        dataRelatedProps["dataNames"] = stackProps.dataNames
        dataRelatedProps["typeBarChart"] = stackProps.typeBarChart
        dataRelatedProps["visibilityLegend"] = stackProps.visibilityLegend
        dataRelatedProps["legendPosition"] = stackProps.legendPosition
      }
    }
  } else if (dataArgument.valueData !== "" && dataArgument.categoryData === "") {
    const widgetData = dataArgument?.specificWidgetData?.data
    if (widgetData) {
      const transformData = getTransformedData(
        widgetInfo,
        widgetData,
        fieldsData,
        partialTrad,
        dataOptions
      )

      dataRelatedProps["dataChart"] = labelAndTransformData(transformData, widgetInfo?.data)
    }

    // dataRelatedProps["valueData"] = dataArgument.valueData

    // 🚨 Pas de groupBy → utiliser les labels comme catégories
    // const categoryLabels = dataArgument.valueData.map(fieldUUID => {
    //   const field = fieldsData.find(f => f.uuid === fieldUUID)
    //   return field?.label || fieldUUID // fallback sur UUID si pas trouvé
    // })

    // dataRelatedProps["categoryData"] = categoryLabels

    dataRelatedProps["valueData"] = ["valueHisto"]
    dataRelatedProps["categoryData"] = ["labelAxeX"]

    dataRelatedProps["forceDisplayPercentSymbol"] = aWidgetValueDataHasOverallPercentage(widgetInfo)
  } else if (widgetHasNoData) {
    const defaultDataChart = getDefaultDataWidget(widgetType)
    dataRelatedProps = { ...dataRelatedProps, ...defaultDataChart }
  }

  return dataRelatedProps
}

export const labelAndTransformData = (rawData, metadata) => {
  const idToLabelMap = {}
  metadata.forEach(item => {
    idToLabelMap[item.valueId] = item.name
  })

  // Étape 1 : enrichir avec les labels
  const labeledData = rawData.map(entry => {
    const newEntry = { ...entry }
    for (const key in entry) {
      if (idToLabelMap[key]) {
        newEntry[`label_${key}`] = idToLabelMap[key]
      }
    }
    return newEntry
  })

  // Étape 2 : transformer les données pour l'histogramme
  const resultats = {}

  labeledData.forEach(reponse => {
    for (const key in reponse) {
      if (key.startsWith("label_")) continue

      const valeur = reponse[key]
      if (valeur === "" || valeur === null || valeur === undefined || resultats[key]) continue

      const labelKey = `label_${key}`
      const label = reponse[labelKey] || ""

      resultats[key] = {
        [key]: valeur,
        [labelKey]: label,
        labelAxeX: label,
        valueHisto: valeur,
      }
    }
  })

  return Object.values(resultats)
}

export const aWidgetValueDataHasOverallPercentage = widgetInfo => {
  if (widgetInfo?.data === undefined) return false
  return widgetInfo.data.some(valueData => valueData.calculus === "overallPercentage")
}

export const getNewWidgetPropsToInjectInComp = (
  widgetInfo,
  comparisonValues,
  fieldsData,
  businessId,
  token
) => {
  var newWidgetProps = {}
  var widgetId = widgetInfo?._id?.$oid

  // widgetType
  const isHorizontalBarChart = widgetInfo.widgetType === "horizontalBarChart"
  const isStackedBarChart = widgetInfo.widgetType === "stackedBar"
  const isImage = widgetInfo.widgetType === "image"
  const isPieChart = widgetInfo.widgetType === "pieChart"
  const isCounter = widgetInfo.widgetType === "counter"
  // const isTable = widgetInfo.widgetType === "table"
  const isTable = [
    "table",
    "ag_table",
    "ganttChart",
    "ganttChartV2",
    "googleMap",
    "caseCardMSF",
  ].includes(widgetInfo.widgetType)
  const isLineChart = widgetInfo.widgetType === "lineChart"
  const isHistogram = widgetInfo.widgetType === "histogram"

  const enableExportMenu = widgetInfo?.widgetProps?.enableExportMenu
  const isFullScreenable = widgetInfo?.widgetProps?.isFullScreenable
  const titleIsEmpty = widgetInfo?.title === ""

  var avoidButtonSuperposition = isTable && isFullScreenable && enableExportMenu && titleIsEmpty
  if (avoidButtonSuperposition) {
    newWidgetProps["exportMenuPosition"] = { top: "25px", right: "5px" }
  } else if (isTable) {
    newWidgetProps["exportMenuPosition"] = {}
  }
  var dataLength = 0
  if (Array.isArray(widgetInfo?.data) && widgetInfo.data?.length) {
    dataLength = widgetInfo.data?.length
  }
  var percentageCalculus = ["cumPercentage", "overallPercentage"]
  var calculusIsPercentage =
    Boolean(dataLength) && percentageCalculus.includes(widgetInfo.data[0]?.calculus)

  newWidgetProps["showAsPercentage"] = calculusIsPercentage

  if (
    isHorizontalBarChart &&
    !widgetInfo?.widgetProps?.typeBarChart &&
    !widgetInfo?.widgetProps?.barOrientation
  ) {
    newWidgetProps["typeBarChart"] = "histogram"
    newWidgetProps["barOrientation"] = "horizontal"
  } else if (isStackedBarChart && !widgetInfo?.widgetProps?.typeBarChart) {
    newWidgetProps["typeBarChart"] = "stackedBar"
  }

  if (isImage) {
    if (widgetId != "widgetBeingEdited") {
      if (!widgetInfo.hasOwnProperty("temporaryId") && widgetInfo?.isUploadedImage) {
        const image = savedWidgetImage(widgetInfo, widgetId)
        // Image du stockage GRC (lot 5) : lue par le conteneur `Widget` (`loadWidgetImage`, sous sa
        // page) — jamais sur la route legacy, où elle n'existe pas.
        if (image.storage !== "v2")
          getPageImageUrl(businessId, image.id, undefined, token, image.directory).then(urlImage => {
            newWidgetProps["urlImage"] = urlImage
          })
      }
      newWidgetProps["isEditing"] = false
    } else {
      newWidgetProps["isEditing"] = true
    }
  }

  var isCompared =
    comparisonValues && Object.keys(comparisonValues)?.length > 0 && widgetInfo.data?.length >= 2

  if (isCompared) {
    if (isPieChart) {
      var newTypeChart = "doublePieChart"
      if (widgetInfo?.widgetProps?.typeChart === "semiPieChart") newTypeChart = "doubleSemiPieChart"
      var newPropsToUpdate = getNewPieAnglePropsOnTypeChartChange(
        widgetInfo?.widgetProps?.typeChart,
        newTypeChart
      )
      newWidgetProps = {
        ...newWidgetProps,
        ...newPropsToUpdate,
        typeChart: newTypeChart,
      }
    } else if (isCounter) {
      var newperiodToDisplay = getPeriodToDisplayFormComparisonValues(comparisonValues)
      newWidgetProps = {
        ...newWidgetProps,
        periodToDisplay: newperiodToDisplay,
      }
    }

    //DATANAMES
    var newDataNames = []
    var dataValues = [...widgetInfo.data]
    dataValues.forEach(dataValue => {
      var dataNameToPush = ""
      if (dataValue?.name && dataValue?.name !== "") {
        dataNameToPush = dataValue.name
      } else {
        let field = getFieldFromUUID(fieldsData, dataValue.uuid)
        if (field?.name) dataNameToPush = field?.name
      }
      newDataNames.push(dataNameToPush)
    })

    newWidgetProps["dataNames"] = newDataNames
  }

  return newWidgetProps
}

export const getNewPieAnglePropsOnTypeChartChange = (oldType, newType) => {
  var newProps = {}
  var shouldUpdateAngle = true
  var semiTypeCharts = ["semiPieChart", "doubleSemiPieChart"]
  var fullTypeCharts = ["pieChart", "doublePieChart"]
  if (oldType === undefined || newType === undefined) {
    shouldUpdateAngle = true
  } else {
    shouldUpdateAngle = Boolean(semiTypeCharts.includes(oldType) ^ semiTypeCharts.includes(newType))
  }

  if (!shouldUpdateAngle) return newProps

  if (semiTypeCharts.includes(newType)) {
    newProps["startAngle"] = -90
    newProps["endAngle"] = 90
  } else if (fullTypeCharts.includes(newType)) {
    newProps["startAngle"] = 0
    newProps["endAngle"] = 360
  }

  return newProps
}

export const getNewFieldCondition = (conditions, comparisonValues, isFirst) => {
  var widgetIdKeys = Object.keys(comparisonValues)
  var comparisonValue = comparisonValues[widgetIdKeys]

  var fieldUUID = comparisonValue.fieldUUID
  var values = isFirst ? comparisonValue.firstValue : comparisonValue.secondValue
  var duplicateWidgetConditions = widgetIdKeys?.length > 0 && Array.isArray(conditions?.filters)
  var filtersToSpread = duplicateWidgetConditions ? conditions.filters : []

  return {
    operation: "and",
    filters: [
      ...filtersToSpread,
      {
        conditionId: "conditionIdCompared1",
        fieldInformation: {
          label: "Test",
          color: "#b820ad",
        },
        fieldUUID: fieldUUID,
        operation: comparisonValue.operation[0],
        value: values[0],
      },
      {
        conditionId: "conditionIdCompared2",
        fieldInformation: {
          label: "Test",
          color: "#b820ad",
        },
        fieldUUID: fieldUUID,
        operation: comparisonValue.operation[1],
        value: values[1],
      },
    ],
  }
}

export const getDateToDisplayFromNanoSec = nanoSec => {
  if (typeof nanoSec !== "number") return ""
  const ONE_MILLION = 1000000
  var dateInMilliSec = nanoSec / ONE_MILLION
  const start = new Date(dateInMilliSec)

  var date = addZeroBeforeNumber(start.getDate())
  var month = addZeroBeforeNumber(start.getMonth() + 1)
  var year = start.getFullYear()
  var yearStr = `${year}`
  var smallYear = yearStr.substring(2, 4)
  return `${date}/${month}/${smallYear}`
}

export const getPeriodToDisplayFormArray = (valueArray, isFirst = true) => {
  if (valueArray !== undefined && Array.isArray(valueArray) && valueArray?.length === 2) {
    var startValue = getDateToDisplayFromNanoSec(valueArray[0])
    var endValue = getDateToDisplayFromNanoSec(valueArray[1])
  }
  var period = `P${isFirst ? "1" : "2"}`
  return `${period}: From ${startValue} to ${endValue}`
}

export const getPeriodToDisplayFormComparisonValues = comparisonValues => {
  var comparisonKey = Object.keys(comparisonValues)
  if (comparisonKey?.length === 0) return ["", ""]

  var comparisonFirstValue = comparisonValues[comparisonKey]
  var firstPeriod = getPeriodToDisplayFormArray(comparisonFirstValue.firstValue, true)
  var secondPeriod = getPeriodToDisplayFormArray(comparisonFirstValue.secondValue, false)
  return [firstPeriod, secondPeriod]
}
export const getMinAndMaxLimitFromComparison = comparisonValues => {
  if (comparisonValues === undefined) return [0, 0]

  var comparisonKeys = Object.keys(comparisonValues)
  if (comparisonKeys?.length === 0) return [0, 0]
  var firstKey = comparisonKeys[0]
  var comparisonContent = comparisonValues[firstKey]
  var hasFirstAndSecondValue =
    comparisonContent.hasOwnProperty("firstValue") &&
    comparisonContent.hasOwnProperty("secondValue")
  var firstAndSecondAreArray =
    hasFirstAndSecondValue &&
    Array.isArray(comparisonContent.firstValue) &&
    Array.isArray(comparisonContent.secondValue)
  var fourDataAreAvailable =
    firstAndSecondAreArray &&
    comparisonContent.firstValue?.length === 2 &&
    comparisonContent.secondValue?.length === 2

  if (!fourDataAreAvailable) return [0, 0]

  var nanoTimestampList = [...comparisonContent.firstValue, ...comparisonContent.secondValue]

  return [Math.min(...nanoTimestampList), Math.max(...nanoTimestampList)]
}

export const widgetCanBeCompared = widgetInfo => {
  return [undefined, true].includes(widgetInfo?.canBeCompared)
}

export const widgetCanBeFiltered = widgetInfo => {
  return [undefined, true].includes(widgetInfo?.canBeFiltered)
}

export const getComparedWidget = (widgetInfo, comparisonValues, fieldsData) => {
  var comparisonValuesRelated = getRelatedPageFilterOrComparison(
    widgetInfo,
    comparisonValues,
    fieldsData
  )
  var newWidgetInfoCompared = { ...widgetInfo }
  var canBeCompared = widgetCanBeCompared(widgetInfo)
  if (Object.keys(comparisonValuesRelated)?.length && canBeCompared) {
    var [minComparison, maxComparison] = getMinAndMaxLimitFromComparison(comparisonValuesRelated)
    var newData = []
    newWidgetInfoCompared.data.forEach(valueDataTemp => {
      var newFirstValueData = {
        ...valueDataTemp,
        name: `P1: ${valueDataTemp.name}`,
        conditions: getNewFieldCondition(valueDataTemp.conditions, comparisonValues, true),
        valueId: `${valueDataTemp.valueId}Compared1`,
      }
      var newSecondValueData = {
        ...valueDataTemp,
        name: `P2: ${valueDataTemp.name}`,
        conditions: getNewFieldCondition(valueDataTemp.conditions, comparisonValues, false),
        valueId: `${valueDataTemp.valueId}Compared2`,
      }
      newData.push(newFirstValueData)
      newData.push(newSecondValueData)
    })
    newWidgetInfoCompared["data"] = newData

    const isDoublePieChart = widgetInfo.widgetType === "pieChart" && widgetInfo.data?.length >= 2
    const isWidgetNeedToSwitchInComparison = ["multiGauge"].includes(widgetInfo.widgetType)

    if (isDoublePieChart || isWidgetNeedToSwitchInComparison) {
      newWidgetInfoCompared["widgetType"] = "table"
    }

    //conditions
    var previousFilter = widgetInfo?.conditions?.filters ? widgetInfo?.conditions?.filters : []
    var comparisonKeys = Object.keys(comparisonValuesRelated)
    var firstKey = comparisonKeys[0]
    var firstComparison = comparisonValuesRelated[firstKey]
    var { fieldUUID } = firstComparison
    if (fieldUUID !== undefined) {
      newWidgetInfoCompared["conditions"] = {
        operation: "and",
        filters: [
          ...previousFilter,
          {
            conditionId: "38beb57e-6d04-416d-b0ae-b8f44229num1",
            fieldUUID: fieldUUID,
            operation: ">",
            value: minComparison !== undefined ? minComparison : 0,
          },
          {
            conditionId: "38beb57e-6d04-416d-b0ae-b8f44229num2",
            fieldUUID: fieldUUID,
            operation: "<",
            value: maxComparison !== undefined ? maxComparison : 0,
          },
        ],
      }
    }
  }
  return { ...newWidgetInfoCompared }
}

export const getFieldUuidFromValueId = (widgetInfo = {}, fieldValueId = "") => {
  if (!Array.isArray(widgetInfo?.data)) return ""
  if (!widgetInfo?.data) return ""
  var fieldFound = widgetInfo?.data.find(data => data?.valueId === fieldValueId)
  var fieldUuid = fieldFound?.uuid
  return fieldUuid ? fieldUuid : ""
}

export const getListOfFieldsUUID = (listValueId = [], widgetInfo = {}) => {
  return listValueId.map(value => getFieldUuidFromValueId(widgetInfo, value))
}

/**
 * Spécifique à PRNSN
 * @description Get un widget dans le layout par un number
 * @param {Array} widgets
 * @param {Object} layout
 * @param {Number} position
 */
export const getWidgetPageLayout = (widgets, layout, position) => {
  const widget = widgets.filter(wgt => wgt?._id?.$oid === layout[position]?.i)

  if (widget[0])
    return {
      ...widget[0],
    }
}

/**
 * @description Generate an array of payloads adapt to update data source, from pipeline widget
 * @param {Object} widgetInfo
 * @param {Array} fieldsData
 * @param {Array} moves
 * @return Return an array
 */
const getPayloadsToUpdateDataSource = (widgetInfo, fieldsData, moves) => {
  if (!widgetInfo || !Array.isArray(fieldsData) || !Array.isArray(moves)) return
  const { groupBy, data } = widgetInfo
  if (!groupBy || !data) return
  const dataFieldSelected = fieldsData.find(field => field?.uuid === data[0].uuid)
  const dataFieldUuid = dataFieldSelected?.uuid
  const groupByFieldSelected = fieldsData.find(field => field?.uuid === groupBy[0].uuid)
  const groupByUuid = groupByFieldSelected?.uuid
  const associateSourceName = groupByFieldSelected?.dataSource?.name

  const movesGroupByDestination = {}
  const destinations = moves.map(move => move?.destinationColumnName)
  const uniqDestinations = [...new Set(destinations)]
  uniqDestinations.forEach(destination => {
    const filteredMoves = moves.filter(move => move?.destinationColumnName === destination)
    movesGroupByDestination[destination] = filteredMoves
  })

  const movesEntries = Object.entries(movesGroupByDestination)
  const payloads = movesEntries.map(([destination, moves]) => {
    const filters = moves.map(move => {
      const dataValue = move?.movingElement?.content
      return { fieldUUID: dataFieldUuid, operation: "==", value: dataValue }
    })
    const payload = {
      action: "updateSamples",
      dataSourceCategory: "excel",
      dataSourceName: associateSourceName,
      data: { [groupByUuid]: destination },
      selector: {
        operation: "or",
        filters: filters,
      },
    }

    return payload
  })

  return payloads
}
